/**
 * AtlasEditModeModule — ONE Edit Mode, one screen (F2).
 *
 * Explore ↔ Edit is a hard mode switch. In Edit Mode the editor owns the
 * camera (fly: WASD + Space/Ctrl, SHIFT boost; hold RMB to look), the cursor
 * (free, for picking), the selection, and ONE undo stack. Everything you
 * need lives on the screen — a top toolbar of tools and a right-hand context
 * panel whose contents follow the active tool — instead of the old scatter
 * of windows (admin palette/outliner, Build Mode v2, Forge Canvas, Uploads).
 *
 * Tools (number row): 1 Select · 2 Move · 3 Rotate · 4 Scale · 5 Place ·
 * 6 Sculpt · 7 Paint · 8 Interior · 9 Script · 0 Upload.
 *
 * Backends are the PROVEN reducers, chosen per world: freeform parts
 * (personal / new worlds — rezPart/rezCatalogPart/editPart/deletePart, the
 * BuildCommandStack undo), admin placements (Commons — adminPlace/Transform/
 * DeleteObject, wrapped in the same stack), deeds (StructurePlacement ghost),
 * uploads (beginUpload → rezUploadedPart), terrain strokes (terrainSculpt),
 * interiors (setInteriorMarker), object scripts (setWorldScript + the new
 * on_interact hook). Permission = capsIn(currentWorld): the owner of a
 * personal/new world, an admin/build grant, or — for the Commons — an
 * explicit admin role (server-admin rule). The server re-checks everything.
 */
import { padSnap, padPressed, padRepeat, padRumble } from '../lib/gamepad.js';
import { editNudge, type NudgeTool } from '../lib/edit_nudge.js';
import * as THREE from 'three/webgpu';
import { TransformControls } from 'three/addons/controls/TransformControls.js';
import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { makeSystem, Phase } from '../engine-compat.js';
import { capsIn, type WorldCaps } from '../lib/world_caps.js';
import { groundYIn } from '../lib/grounding.js';
import { iconMarkup } from '../lib/icons.js';
import { ASSET_STORE_BASE } from '../config.js';
import { BuildCommandStack, EditPartCmd, DeletePartCmd, RezCmd, GroupCmd, snapshotPart, type BuildCommand, type PartRowSnapshot } from '../lib/build_commands.js';
import { capturePrefab, instantiatePrefab, loadPrefabs, savePrefabs, upsertPrefab, type PrefabDef } from '../lib/prefabs.js';
import { worldEnv, DEFAULT_WORLD_ENV, WEATHERS, AMBIENCE_KEYS, DAY_LENGTHS, todToClock, clockToTod, type WorldEnv } from '../lib/world_env.js';
import { DEVICE_KINDS, DEVICE_GROUPS, DEVICE_BY_KIND, deviceConfig, parseCameraKeys, parseDialogue, parseSchedule, SAMPLE_DIALOGUE, num, str, bool, type FieldSpec, type DialogueTree, type ScheduleEntry } from '../lib/devices.js';
import type { DeviceModule, DeviceRow } from './DeviceModule.js';
import { REZ_PRIM_TYPES } from '@shared/types/freeform.js';
import { BIOMES } from '../../../shared/terrain.js';
import { enterables } from '../lib/building_interiors.js';
import type { FreeformBuildModule } from './FreeformBuildModule.js';
import type { AtlasAdminEditorModule } from './AtlasAdminEditorModule.js';
import type { AssetModule } from './AssetModule.js';
import { TYPE_SCALE } from './StructureRenderModule.js';
import { prefabAssetId } from '../lib/player_models.js';
import { fitScaleFor } from '../lib/structure_pivot.js';
import { PLAYER_MODELS } from '../lib/player_models.js';
import { NPC_TEMPLATES, npcTemplate } from '../lib/npc_script_templates.js';
import { apiFor } from '../lib/ide/lua_api.js';
import type { NpcUnitModule } from './NpcUnitModule.js';

/** Quiet time before a run of nudges becomes ONE undo step. */
const PAD_NUDGE_COMMIT_S = 0.35;

type Tool = 'select' | 'move' | 'rotate' | 'scale' | 'place' | 'sculpt' | 'paint' | 'interior' | 'script' | 'upload' | 'npc' | 'versona' | 'env' | 'audio' | 'devices';
const TOOLS: Array<{ id: Tool; key: string; kbd?: string; label: string; icon: string; hint: string }> = [
  { id: 'select', key: 'Digit1', label: 'Select', icon: 'arrow', hint: 'Click an object to select · Delete removes · Ctrl+D duplicates' },
  { id: 'move', key: 'Digit2', label: 'Move', icon: 'swap', hint: 'Drag the arrows · X toggles 1 m snap' },
  { id: 'rotate', key: 'Digit3', label: 'Rotate', icon: 'orbit', hint: 'Drag the rings · X toggles 15° snap' },
  { id: 'scale', key: 'Digit4', label: 'Scale', icon: 'size', hint: 'Drag the handles · X toggles 0.25 snap' },
  { id: 'place', key: 'Digit5', label: 'Place', icon: 'box', hint: 'Pick from the catalog, click the ground to place · R rotates the ghost' },
  { id: 'sculpt', key: 'Digit6', label: 'Sculpt', icon: 'brick', hint: 'LMB raises · Ctrl+LMB lowers · Alt+LMB flattens to the brush height' },
  { id: 'paint', key: 'Digit7', label: 'Paint', icon: 'color', hint: 'Pick a ground layer, LMB paints' },
  { id: 'interior', key: 'Digit8', label: 'Interior', icon: 'home', hint: 'Select a structure and flag it enterable — then E at its door' },
  { id: 'script', key: 'Digit9', label: 'Script', icon: 'code', hint: 'Select an object, write what happens when a player presses E on it' },
  { id: 'upload', key: 'Digit0', label: 'Upload', icon: 'globe', hint: 'Upload a .glb; ready models appear in the Place catalog' },
  { id: 'npc', key: 'Minus', kbd: '-', label: 'NPC', icon: 'people', hint: 'NPC creator — place a blank-slate character and script it (npc.* Lua API)' },
  { id: 'versona', key: 'Equal', kbd: '=', label: 'Versona', icon: 'name', hint: 'Place a living versona — Commons context on the Commons; in your world you author it' },
  { id: 'env', key: 'BracketLeft', kbd: '[', label: 'Environment', icon: 'shade', hint: 'Time of day, weather, fog, sun and ambience for this world — everyone sees it' },
  { id: 'audio', key: 'BracketRight', kbd: ']', label: 'Audio', icon: 'orbit', hint: 'Place ambience / music / sfx zones; select one to move or resize' },
  { id: 'devices', key: 'Semicolon', kbd: ';', label: 'Devices', icon: 'gamepad', hint: 'Lights, FX, signs, doors, buttons, triggers, teleporters, platforms, zones, water, speakers, cinematics, NPC paths, timers — wired by signals' },
];
const SELECT_TOOLS: ReadonlySet<Tool> = new Set<Tool>(['select', 'move', 'rotate', 'scale', 'interior', 'script', 'npc', 'versona', 'audio', 'devices']);
/** NPC presets: a template + data the NPC tool fills in with no code (dialogue tree, schedule). */
const NPC_PRESETS: Array<{ key: string; label: string; name: string; template: string; config: Record<string, unknown> }> = [
  { key: 'custom', label: 'Custom (template only)', name: '', template: 'greeter', config: {} },
  { key: 'shopkeeper', label: 'Shopkeeper — greets, talks wares, closes at night', name: 'Shopkeeper Ondra', template: 'greeter', config: {
    dialogue: { start: 'hi', nodes: [
      { id: 'hi', text: 'Welcome in. Looking for anything in particular?', choices: [{ text: 'What do you sell?', next: 'wares' }, { text: 'Just browsing.', next: '' }] },
      { id: 'wares', text: 'Tools, rations, a few curiosities from the caravans. Prices are fair.', actions: ['state selling'], choices: [{ text: 'Thanks.', next: '' }] },
    ] },
    schedule: [{ hour: 8, action: 'home' }, { hour: 8, action: 'state', value: 'open' }, { hour: 20, action: 'state', value: 'closed' }, { hour: 21, action: 'wander', x: 6 }],
  } },
  { key: 'guard', label: 'Guard — patrols a path, challenges at night', name: 'Watch Captain Rhen', template: 'guard', config: { schedule: [{ hour: 6, action: 'state', value: 'on duty' }, { hour: 22, action: 'state', value: 'night watch' }] } },
  { key: 'guide', label: 'Guide — explains the world, points the way', name: 'Guide Ilya', template: 'greeter', config: {
    dialogue: { start: 'hi', nodes: [
      { id: 'hi', text: 'New here? I can point you around.', choices: [{ text: 'Where should I go first?', next: 'where' }, { text: 'Who built this place?', next: 'who' }, { text: 'I know my way.', next: '' }] },
      { id: 'where', text: 'Follow the lamps to the plaza — everything starts there.', actions: ['emote wave'], choices: [{ text: 'Thanks!', next: '' }] },
      { id: 'who', text: 'The owner of this world. Ask them — they love to talk about it.', choices: [{ text: 'I will.', next: 'hi' }] },
    ] },
  } },
  { key: 'quest', label: 'Quest giver — stages remembered per player', name: 'Old Maren', template: 'quest', config: { dialogue: SAMPLE_DIALOGUE } },
];
interface DeviceXf { posX: number; posY: number; posZ: number; yaw: number; sx: number; sy: number; sz: number }
const deviceArgs = (r: DeviceRow, xf: DeviceXf) => ({ deviceId: r.deviceId, name: r.name, configJson: r.configJson, enabled: r.enabled, ...xf });
class DeviceXfCmd implements BuildCommand {
  readonly label: string;
  constructor(private readonly row: DeviceRow, private readonly before: DeviceXf, private readonly after: DeviceXf) { this.label = `Move ${row.kind}`; }
  async execute(): Promise<void> { await atlas.conn!.reducers.editDevice(deviceArgs(this.row, this.after)); }
  async undo(): Promise<void> { await atlas.conn!.reducers.editDevice(deviceArgs(this.row, this.before)); }
}
class DeviceDeleteCmd implements BuildCommand {
  readonly label: string;
  constructor(private readonly row: DeviceRow) { this.label = `Delete ${row.kind}`; }
  async execute(): Promise<void> { await atlas.conn!.reducers.deleteDevice({ deviceId: this.row.deviceId }); }
  async undo(): Promise<void> {
    const r = this.row; // re-placed under a NEW id
    await atlas.conn!.reducers.placeDevice({ worldId: r.worldId, kind: r.kind, name: r.name, posX: r.posX, posY: r.posY, posZ: r.posZ, yaw: r.yaw, sx: r.sx, sy: r.sy, sz: r.sz, configJson: r.configJson });
  }
}
const MARQUEE_MIN_PX = 6;
const AUDIO_KINDS = ['ambience', 'music', 'sfx'] as const;
interface ZoneRow { zoneId: bigint; worldId: bigint; name: string; audioAssetId: bigint; kind: string; shapeType: string; cx: number; cy: number; cz: number; radius: number; sx: number; sy: number; sz: number; gain: number; loop: boolean; fadeDuration: number; priority: number }
const zoneArgs = (z: ZoneRow) => ({ worldId: z.worldId, name: z.name, audioAssetId: z.audioAssetId, kind: z.kind, shapeType: z.shapeType, cx: z.cx, cy: z.cy, cz: z.cz, radius: z.radius, sx: z.sx, sy: z.sy, sz: z.sz, gain: z.gain, loop: z.loop, fadeDuration: z.fadeDuration, priority: z.priority });
/** Audio zone edit/create as an undoable command (createAudioZone upserts by name). */
class ZoneUpsertCmd implements BuildCommand {
  constructor(readonly label: string, private readonly before: ZoneRow | null, private readonly after: ZoneRow) {}
  async execute(): Promise<void> { await atlas.conn!.reducers.createAudioZone(zoneArgs(this.after)); }
  async undo(): Promise<void> {
    if (this.before) { await atlas.conn!.reducers.createAudioZone(zoneArgs(this.before)); return; }
    const row = [...atlas.conn!.db.audioZone.iter()].find((z) => z.worldId === this.after.worldId && z.name === this.after.name);
    if (row) await atlas.conn!.reducers.deleteAudioZone({ zoneId: row.zoneId });
  }
}
class ZoneDeleteCmd implements BuildCommand {
  readonly label: string;
  constructor(private readonly row: ZoneRow) { this.label = `Delete zone ${row.name}`; }
  async execute(): Promise<void> { await atlas.conn!.reducers.deleteAudioZone({ zoneId: this.row.zoneId }); }
  async undo(): Promise<void> { await atlas.conn!.reducers.createAudioZone(zoneArgs(this.row)); }
}
const VERSONA_ARCHETYPES = ['civilian', 'vendor', 'guard', 'trainer', 'entertainer'] as const;
const NPC_SCALE_MIN = 0.25, NPC_SCALE_MAX = 4;

interface VersonaDraft {
  name: string; archetype: string; persona: string; context: string; greeting: string; deflect: string; topicsText: string;
  llm: boolean; strict: boolean; temperature: number; maxWords: number; wanderM: number;
}
const DEFAULT_VERSONA_DRAFT: VersonaDraft = {
  name: '', archetype: 'civilian', persona: '', context: '', greeting: '', deflect: "I wouldn't know about that.",
  topicsText: '', llm: true, strict: true, temperature: 0.2, maxWords: 45, wanderM: 0,
};
/** "Label :: reply" lines → the profile's topicsJson. */
function topicsTextToJson(text: string): string {
  const out: Array<{ k: string; l: string; r: string }> = [];
  for (const line of text.split('\n')) {
    const i = line.indexOf('::');
    if (i < 0) continue;
    const l = line.slice(0, i).trim(), r = line.slice(i + 2).trim();
    if (!l || !r) continue;
    out.push({ k: l.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 32) || `t${out.length + 1}`, l, r });
  }
  return JSON.stringify(out);
}
function topicsJsonToText(json: string): string {
  try { return (JSON.parse(json || '[]') as Array<{ l: string; r: string }>).map((t) => `${t.l} :: ${t.r}`).join('\n'); } catch { return ''; }
}

const FLY_SPEED = 24, FLY_BOOST = 90;
const LOOK_SENS = 0.0022;
const STROKE_HZ = 7;
const COMMONS_LAYERS = ['Regolith plains', 'Crystal forest', 'Dune sand', 'Rocky highlands', 'Moss wetland', 'Fungal basin'];
const PERSONAL_LAYERS = ['Regolith', 'Moss', 'Dirt'];
const CIV_TITLE: Record<string, string> = { a: 'Aurelian Concord', b: 'Borean Compact' };

type SelKind = 'part' | 'placement' | 'npc' | 'versona' | 'zone' | 'device';
interface Selection { kind: SelKind; id: string; mesh: THREE.Object3D }
/** A member's pose + row snapshot captured at multi-drag start. */
interface MemberBefore { s: Selection; pos: THREE.Vector3; quat: THREE.Quaternion; scale: THREE.Vector3; xf: XF; snap: PartRowSnapshot | null; npc: NpcRowLite | null; zone: ZoneRow | null; dev: DeviceRow | null }

/** adminPlacement transform edit as an undoable command (the one stack). */
class PlacementXfCmd implements BuildCommand {
  constructor(readonly label: string, private readonly id: bigint, private readonly before: XF, private readonly after: XF) {}
  async execute(): Promise<void> { await atlas.conn!.reducers.adminTransformObject({ placementId: this.id, ...this.after }); }
  async undo(): Promise<void> { await atlas.conn!.reducers.adminTransformObject({ placementId: this.id, ...this.before }); }
}
class PlacementDeleteCmd implements BuildCommand {
  readonly label: string;
  constructor(private readonly row: { placementId: bigint; worldId: bigint; prefabKey: string; kind: string } & XF) {
    this.label = `Delete ${row.prefabKey}`;
  }
  async execute(): Promise<void> { await atlas.conn!.reducers.adminDeleteObject({ placementId: this.row.placementId }); }
  async undo(): Promise<void> {
    const r = this.row;
    await atlas.conn!.reducers.adminPlaceObject({
      worldId: r.worldId, prefabKey: r.prefabKey, kind: r.kind,
      posX: r.posX, posY: r.posY, posZ: r.posZ, rotX: r.rotX, rotY: r.rotY, rotZ: r.rotZ, rotW: r.rotW,
      scaleX: r.scaleX, scaleY: r.scaleY, scaleZ: r.scaleZ,
    });
  }
}
/** npc_unit transform edit (editNpc keeps name/body/config — the transform is the diff). */
interface NpcXf { posX: number; posY: number; posZ: number; yaw: number; scale: number }
interface NpcRowLite { npcId: bigint; worldId: bigint; name: string; modelKey: string; scriptSrc: string; enabled: boolean; configJson: string }
class NpcXfCmd implements BuildCommand {
  readonly label = 'Move NPC';
  constructor(private readonly row: NpcRowLite, private readonly before: NpcXf, private readonly after: NpcXf) {}
  private apply(x: NpcXf): Promise<void> {
    const r = this.row;
    return atlas.conn!.reducers.editNpc({ npcId: r.npcId, name: r.name, modelKey: r.modelKey, configJson: r.configJson, ...x });
  }
  async execute(): Promise<void> { await this.apply(this.after); }
  async undo(): Promise<void> { await this.apply(this.before); }
}
class NpcDeleteCmd implements BuildCommand {
  readonly label: string;
  constructor(private readonly row: NpcRowLite & NpcXf) { this.label = `Delete NPC ${row.name}`; }
  async execute(): Promise<void> { await atlas.conn!.reducers.deleteNpc({ npcId: this.row.npcId }); }
  async undo(): Promise<void> {
    const r = this.row; // re-placed under a NEW id (like placements)
    await atlas.conn!.reducers.placeNpc({ worldId: r.worldId, name: r.name, modelKey: r.modelKey, posX: r.posX, posY: r.posY, posZ: r.posZ, yaw: r.yaw, scale: r.scale, scriptSrc: r.scriptSrc, configJson: r.configJson });
  }
}
class VersonaMoveCmd implements BuildCommand {
  readonly label = 'Move versona';
  constructor(private readonly versonaId: bigint, private readonly before: { x: number; z: number }, private readonly after: { x: number; z: number }) {}
  async execute(): Promise<void> { await atlas.conn!.reducers.moveVersona({ versonaId: this.versonaId, ...this.after }); }
  async undo(): Promise<void> { await atlas.conn!.reducers.moveVersona({ versonaId: this.versonaId, ...this.before }); }
}
interface XF { posX: number; posY: number; posZ: number; rotX: number; rotY: number; rotZ: number; rotW: number; scaleX: number; scaleY: number; scaleZ: number }
const xfOf = (o: THREE.Object3D): XF => ({
  posX: o.position.x, posY: o.position.y, posZ: o.position.z,
  rotX: o.quaternion.x, rotY: o.quaternion.y, rotZ: o.quaternion.z, rotW: o.quaternion.w,
  scaleX: o.scale.x, scaleY: o.scale.y, scaleZ: o.scale.z,
});

export class AtlasEditModeModule implements IModule {
  readonly name = 'AtlasEditMode';

  private app: App | null = null;
  private canvas: HTMLCanvasElement | null = null;
  private scene: THREE.Scene | null = null;
  private active = false;
  private zoneTablesSubbed = false;
  private tool: Tool = 'select';
  private caps: WorldCaps = capsIn(null);

  // camera / input
  private keys = new Set<string>();
  private looking = false;
  private readonly fwd = new THREE.Vector3();
  private readonly right = new THREE.Vector3();
  private readonly up = new THREE.Vector3(0, 1, 0);
  private readonly move = new THREE.Vector3();
  private readonly ray = new THREE.Raycaster();
  private readonly ndc = new THREE.Vector2();
  private cursorX = 0; private cursorY = 0;
  private lmbDown = false;

  // selection + gizmo + undo (sel = primary; sels = every member; N>1 ⇒ the gizmo drives a pivot)
  private sel: Selection | null = null;
  private sels: Selection[] = [];
  private gizmo: TransformControls | null = null;
  private gizmoHelper: THREE.Object3D | null = null;
  private selBoxes: THREE.BoxHelper[] = [];
  private pivot: THREE.Object3D | null = null;
  private pivotStart: { pos: THREE.Vector3; quat: THREE.Quaternion; scale: THREE.Vector3 } | null = null;
  private multiBefore: MemberBefore[] | null = null;
  private dragBefore: XF | null = null;
  private dragPartBefore: PartRowSnapshot | null = null;
  private press: { x: number; y: number; shift: boolean } | null = null;
  private marquee: { sx: number; sy: number; el: HTMLDivElement } | null = null;
  private snap = false;
  private readonly stack = new BuildCommandStack();
  // audio zones (markers while editing) + environment draft
  private readonly zoneRoot = new THREE.Group();
  private zoneMarkers = new Map<string, THREE.Mesh>();
  private zoneGeo: THREE.SphereGeometry | null = null;
  private zoneDraft = { assetId: 0n as bigint, kind: 'ambience', radius: 12, gain: 0.8, loop: true, fade: 1.5 };
  private envDraft: (WorldEnv & { cycleSeconds: number; weather: string }) | null = null;
  private outlinerFilter = '';
  private zoneSyncCountdown = 0;

  // tools state
  private brushR = 12; private brushStrength = 2.5; private paintLayer = 0; private paintStrength = 0.8;
  private brushRing: THREE.Mesh | null = null;
  private strokeAcc = 0;
  private ghost: THREE.Object3D | null = null;
  private ghostYaw = 0;
  private placeChoice: { kind: 'prim' | 'prefab' | 'upload' | 'npc' | 'versona' | 'prefabset' | 'zone' | 'device'; key: string; assetId?: bigint; scale: number; prefab?: PrefabDef } | null = null;
  private deviceDrafts = new Map<string, { name: string; enabled: boolean; cfg: Record<string, unknown>; sx: number; sy: number; sz: number }>();
  private npcCfgDrafts = new Map<string, Record<string, unknown>>();
  private rulesDraft: { objective: string; roundSeconds: number; respawnMode: string; scoreTopic: string } | null = null;
  private measuredAsset: bigint | null = null; // asset ref held while a structure's measured ghost is up
  // people tools — drafts persist across panel re-renders
  private npcDraft = { name: '', modelKey: PLAYER_MODELS[0].key, template: 'greeter', preset: 'custom' };
  private versonaDraft: VersonaDraft = { ...DEFAULT_VERSONA_DRAFT };
  private npcScriptDraft = new Map<string, string>(); // npcId → unsaved source
  private showApiRef = false;

  // DOM
  private root: HTMLDivElement | null = null;
  private bar: HTMLDivElement | null = null;
  private panel: HTMLDivElement | null = null;
  private status: HTMLDivElement | null = null;
  private toolBtns = new Map<Tool, HTMLButtonElement>();

  constructor(private readonly freeform: FreeformBuildModule, private readonly admin: AtlasAdminEditorModule, private readonly assets: AssetModule, private readonly npcs: NpcUnitModule, private readonly devices: DeviceModule) {}

  init(app: App): void {
    this.app = app;
    this.scene = app.world.scene;
    this.canvas = app.renderer.native.domElement as HTMLCanvasElement;
    app.world.addSystem(makeSystem('EditModeStep', Phase.Animation, 42, (dt) => this.step(dt)));
    // Capture-phase: the legacy editor hotkeys route HERE while editing is
    // available (B Build Mode, G Forge Canvas, F7 Uploads, F admin fly).
    window.addEventListener('keydown', (e) => this.onKeyCapture(e), true);
    window.addEventListener('keydown', (e) => this.onKey(e, true));
    window.addEventListener('keyup', (e) => this.onKey(e, false));
    window.addEventListener('blur', () => this.keys.clear());
    bus.on('atlas:world-entered', () => { if (this.active) this.exit(); this.refreshCaps(); });
    bus.on('atlas:world-cleared', () => { if (this.active) this.exit(); });
    bus.on('atlas:account-ready', () => this.refreshCaps());
    (window as unknown as Record<string, unknown>).__editMode = {
      enter: () => this.enter(), exit: () => this.exit(), tool: (t: Tool) => this.setTool(t),
      state: () => ({ active: this.active, tool: this.tool, caps: this.caps, sel: this.sel ? `${this.sel.kind}:${this.sel.id}` : null, sels: this.sels.map((m) => `${m.kind}:${m.id}`), undo: this.stack.depth, ghost: this.placeChoice ? `${this.placeChoice.kind}:${this.placeChoice.key}` : null }),
      // ── probe surface (the selection model / tools without pointer events) ──
      selectables: () => this.selectables().map((s) => ({ kind: s.kind, id: s.id })),
      select: (kind: SelKind, id: string, additive = false) => { const s = this.selectables().find((x) => x.kind === kind && x.id === String(id)); if (s) this.select(s, additive); return !!s; },
      selectMany: (list: Array<{ kind: SelKind; id: string }>) => { const all = this.selectables(); const picked = list.map((l) => all.find((x) => x.kind === l.kind && x.id === String(l.id))).filter((x): x is Selection => !!x); if (picked.length) this.setSelection(picked, picked[picked.length - 1]); return picked.length; },
      clear: () => this.clearSelection(),
      outliner: () => this.outlinerEntries().map((e) => e.label),
      dropToFloor: () => this.dropToFloor(),
      deleteSelected: () => this.deleteSelected(),
      duplicateSelected: () => this.duplicateSelected(),
      undo: () => this.stack.undo(), redo: () => this.stack.redo(),
      savePrefab: (name: string) => this.savePrefabNamed(name),
      prefabs: () => loadPrefabs().map((p) => ({ name: p.name, parts: p.parts.length })),
      placeAt: (kind: 'prim' | 'prefab' | 'npc' | 'versona' | 'zone' | 'device' | 'prefabset', key: string, x: number, z: number) => this.placeAtProbe(kind, key, x, z),
      zoneMarkers: () => this.zoneMarkers.size,
      pivot: () => (this.pivot ? { x: this.pivot.position.x, y: this.pivot.position.y, z: this.pivot.position.z } : null),
      nudgeSelection: (dx: number, dy: number, dz: number) => this.nudgeProbe(dx, dy, dz),
      groundY: (x: number, z: number) => (atlas.conn ? groundYIn(atlas.conn, atlas.currentWorldId, x, z) : 0),
    };
  }

  // ─── permissions ─────────────────────────────────────────────────────────

  private refreshCaps(): void { this.caps = capsIn(atlas.currentWorldId); }
  /** May the local player edit the CURRENT world at all? */
  private mayEdit(): boolean { this.refreshCaps(); return !!atlas.conn && atlas.currentWorldId !== null && (this.caps.build || this.caps.admin); }
  private isCommons(): boolean { return atlas.currentWorldId !== null && atlas.currentWorldId === atlas.commonsWorldId; }
  private isFreeform(): boolean { return !this.isCommons(); }

  // ─── mode switch ─────────────────────────────────────────────────────────

  toggle(): void { if (this.active) this.exit(); else this.enter(); }

  enter(): void {
    if (this.active) return;
    if (!this.mayEdit()) {
      bus.emit('atlas:status', { text: this.isCommons()
        ? 'Edit Mode needs a server admin role on the Commons'
        : 'Edit Mode is for worlds you own or hold a build grant in' });
      return;
    }
    if (atlas.spaceFlight || atlas.skyFlight || atlas.hoverRide) { bus.emit('atlas:status', { text: 'Land first — Edit Mode is for feet on the ground' }); return; }
    this.active = true;
    atlas.editMode = true;
    atlas.flyMode = true;           // the avatar parks; we drive the body
    atlas.editorCursorMode = true;  // free cursor; no auto pointer-lock
    if (document.pointerLockElement) document.exitPointerLock();
    this.buildDom();
    if (this.scene && !this.zoneRoot.parent) this.scene.add(this.zoneRoot);
    // (bug sweep) un-scoped tables: subscribe ONCE — every Esc-out / re-enter cycle stacked two more live queries.
    if (!this.zoneTablesSubbed && atlas.conn) {
      this.zoneTablesSubbed = true;
      try { atlas.conn.subscriptionBuilder().subscribe(['SELECT * FROM audio_zone', 'SELECT * FROM world_environment']); } catch { /* pre-publish */ }
    }
    this.syncZoneMarkers(); // markers exist from the first frame (a slow tab would otherwise wait the cadence out)
    this.envDraft = null;
    this.setTool('select');
    bus.emit('atlas:status', { text: `Edit Mode — ${this.isCommons() ? 'the Commons (server admin)' : 'your world'} · Esc exits` });
  }

  exit(): void {
    if (!this.active) return;
    this.clearSelection();
    this.clearGhost();
    this.hideBrush();
    this.active = false;
    this.endMarquee();
    this.clearZoneMarkers();
    if (this.zoneRoot.parent) this.zoneRoot.parent.remove(this.zoneRoot);
    this.npcs.heldNpcId = null;
    this.devices.heldDeviceId = null;
    bus.emit('atlas:ide-close', {}); // the IDE lives inside Edit Mode
    atlas.editMode = false;
    atlas.flyMode = false;
    atlas.editorCursorMode = false;
    this.looking = false;
    this.keys.clear();
    this.root?.remove();
    this.root = null; this.bar = null; this.panel = null; this.status = null;
    this.toolBtns.clear();
    this.canvas?.removeEventListener('pointerdown', this.onPointerDown);
    window.removeEventListener('pointerup', this.onPointerUp);
    window.removeEventListener('pointermove', this.onPointerMove);
    this.canvas?.removeEventListener('contextmenu', this.onContext);
    // Land the body: the avatar resumes from where the camera floated.
    const body = atlas.localMesh;
    const conn = atlas.conn;
    if (body && conn) {
      atlas.localX = body.position.x; atlas.localZ = body.position.z;
      atlas.localY = groundYIn(conn, atlas.currentWorldId, body.position.x, body.position.z);
      body.position.y = atlas.localY;
    }
    bus.emit('atlas:status', { text: 'Back to exploring' });
  }

  // ─── input ───────────────────────────────────────────────────────────────

  private typing(e: KeyboardEvent): boolean {
    const t = e.target instanceof Element ? (e.target as HTMLElement) : null;
    return !!t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable || !!t.closest('.cm-editor'));
  }

  private onKeyCapture(e: KeyboardEvent): void {
    if (e.repeat || this.typing(e)) return;
    if (e.code === 'F2' && !e.ctrlKey && !e.metaKey && !e.altKey) { e.preventDefault(); e.stopImmediatePropagation(); this.toggle(); return; }
    if (atlas.spaceFlight) return;
    // The old doors open the new room — only where the player may edit.
    const legacy = (e.code === 'KeyB' || e.code === 'KeyG' || e.code === 'F7') && !e.ctrlKey && !e.metaKey && !e.altKey;
    if (legacy && this.mayEdit()) { e.preventDefault(); e.stopImmediatePropagation(); if (!this.active) this.enter(); if (e.code === 'F7') this.setTool('upload'); return; }
    if (!this.active) return;
    // Inside Edit Mode: swallow keys other modules would otherwise react to.
    if (['KeyF', 'KeyE', 'KeyT', 'KeyY', 'KeyK', 'KeyZ', 'Tab'].includes(e.code) && !e.ctrlKey && !e.metaKey) { e.preventDefault(); e.stopImmediatePropagation(); }
  }

  private onKey(e: KeyboardEvent, down: boolean): void {
    if (down) this.keys.add(e.code); else this.keys.delete(e.code);
    if (!this.active || !down || e.repeat || this.typing(e)) return;
    for (const t of TOOLS) if (e.code === t.key && !e.ctrlKey && !e.metaKey) { this.setTool(t.id); return; }
    if (e.code === 'Escape') { if (this.sel) this.clearSelection(); else if (this.ghost) this.clearGhost(); else this.exit(); return; }
    if (e.code === 'KeyX' && !e.ctrlKey) { this.snap = !this.snap; this.applySnap(); this.flash(`Snap ${this.snap ? 'on (1 m · 15° · 0.25)' : 'off'}`); return; }
    if ((e.code === 'Delete' || e.code === 'Backspace') && this.sel) { void this.deleteSelected(); return; }
    if (e.code === 'KeyD' && e.ctrlKey && this.sel) { e.preventDefault(); void this.duplicateSelected(); return; }
    if (e.code === 'KeyZ' && e.ctrlKey && !e.shiftKey) { e.preventDefault(); void this.stack.undo().then(() => this.renderPanel()); return; }
    if (e.code === 'KeyZ' && e.ctrlKey && e.shiftKey) { e.preventDefault(); void this.stack.redo().then(() => this.renderPanel()); return; }
    if (e.code === 'KeyY' && e.ctrlKey) { e.preventDefault(); void this.stack.redo().then(() => this.renderPanel()); return; }
    if (e.code === 'KeyR' && this.ghost) { this.ghostYaw += Math.PI / 8; return; }
  }

  private onPointerDown = (e: PointerEvent): void => {
    if (!this.active) return;
    if (e.button === 2) {
      this.looking = true;
      try { this.canvas?.requestPointerLock(); } catch { /* ignore */ }
      return;
    }
    if (e.button !== 0) return;
    const g = this.gizmo as unknown as { dragging: boolean; axis: string | null } | null;
    if (g && (g.dragging || g.axis)) return; // the gizmo owns this press (hovering a handle)
    this.lmbDown = true;
    if (this.ghost) { void this.placeAtGhost(); return; } // any ghost (shape/prefab/NPC/versona/zone) places on click
    if (this.tool === 'sculpt' || this.tool === 'paint') { this.strokeAcc = 1 / STROKE_HZ; return; } // first stroke immediately
    // Click vs drag is decided on release: a still press picks, a drag rubber-bands.
    this.press = { x: e.clientX, y: e.clientY, shift: e.shiftKey };
  };
  private onPointerUp = (e: PointerEvent): void => {
    if (e.button === 2) { this.looking = false; if (document.pointerLockElement === this.canvas) document.exitPointerLock(); }
    if (e.button !== 0) return;
    this.lmbDown = false;
    const press = this.press;
    this.press = null;
    if (this.marquee) {
      const m = this.marquee;
      this.endMarquee();
      const x0 = Math.min(m.sx, e.clientX), x1 = Math.max(m.sx, e.clientX);
      const y0 = Math.min(m.sy, e.clientY), y1 = Math.max(m.sy, e.clientY);
      this.marqueeSelect(x0, y0, x1, y1, e.shiftKey || (press?.shift ?? false));
      return;
    }
    if (press && SELECT_TOOLS.has(this.tool)) this.pick(e, press.shift || e.shiftKey);
  };
  private onPointerMove = (e: PointerEvent): void => {
    if (!this.active) return;
    if (this.looking && document.pointerLockElement === this.canvas) {
      const body = atlas.localMesh;
      const cam = body?.children[0] as THREE.Object3D | undefined;
      atlas.localYaw -= e.movementX * LOOK_SENS;
      if (body) body.rotation.y = atlas.localYaw;
      if (cam) cam.rotation.x = Math.max(-1.5, Math.min(1.5, cam.rotation.x - e.movementY * LOOK_SENS));
      return;
    }
    this.cursorX = e.clientX; this.cursorY = e.clientY;
    if (this.press && !this.marquee && SELECT_TOOLS.has(this.tool)
      && Math.hypot(e.clientX - this.press.x, e.clientY - this.press.y) > MARQUEE_MIN_PX) this.startMarquee(this.press.x, this.press.y);
    if (this.marquee) {
      const m = this.marquee;
      const x = Math.min(m.sx, e.clientX), y = Math.min(m.sy, e.clientY);
      m.el.style.left = `${x}px`; m.el.style.top = `${y}px`;
      m.el.style.width = `${Math.abs(e.clientX - m.sx)}px`; m.el.style.height = `${Math.abs(e.clientY - m.sy)}px`;
    }
  };
  // ─── marquee (rubber-band) ───────────────────────────────────────────────
  private startMarquee(sx: number, sy: number): void {
    const el = document.createElement('div');
    el.style.cssText = 'position:fixed;border:1px dashed var(--ax-good);background:color-mix(in srgb, var(--ax-good) 12%, transparent);z-index:60;pointer-events:none';
    el.style.left = `${sx}px`; el.style.top = `${sy}px`;
    document.body.appendChild(el);
    this.marquee = { sx, sy, el };
  }
  private endMarquee(): void { this.marquee?.el.remove(); this.marquee = null; }

  /** Every selectable thing in the current world with its scene object. */
  private selectables(): Selection[] {
    const conn = atlas.conn;
    const wid = atlas.currentWorldId;
    const out: Selection[] = [];
    if (!conn || wid === null) return out;
    try {
      if (this.isFreeform()) for (const p of conn.db.part.iter()) if (p.worldId === wid) { const m = this.freeform.partMesh(p.partId.toString()); if (m) out.push({ kind: 'part', id: p.partId.toString(), mesh: m }); }
      for (const r of conn.db.adminPlacement.iter()) if (r.worldId === wid) { const m = this.admin.placementMesh(r.placementId.toString()); if (m) out.push({ kind: 'placement', id: r.placementId.toString(), mesh: m }); }
      for (const id of this.npcs.ids()) { const m = this.npcs.unitMesh(id); if (m) out.push({ kind: 'npc', id, mesh: m }); }
      for (const pr of conn.db.versonaProfile.iter()) if (pr.worldId === wid) { const m = this.npcs.markerFor(pr.versonaId.toString()); if (m) out.push({ kind: 'versona', id: pr.versonaId.toString(), mesh: m }); }
      for (const [id, m] of this.zoneMarkers) out.push({ kind: 'zone', id, mesh: m });
      for (const id of this.devices.ids()) { const m = this.devices.deviceMesh(id); if (m) out.push({ kind: 'device', id, mesh: m }); }
    } catch { /* churn */ }
    return out;
  }

  /** Select everything whose projected center falls inside the screen rect. */
  private marqueeSelect(x0: number, y0: number, x1: number, y1: number, additive: boolean): void {
    const cam = atlas.localMesh?.children[0] as THREE.PerspectiveCamera | undefined;
    if (!cam || !this.canvas) return;
    if (x1 - x0 < MARQUEE_MIN_PX && y1 - y0 < MARQUEE_MIN_PX) { if (!additive) this.clearSelection(); return; }
    const r = this.canvas.getBoundingClientRect();
    const v = new THREE.Vector3();
    const picked: Selection[] = additive ? [...this.sels] : [];
    for (const s of this.selectables()) {
      if (picked.some((p) => p.kind === s.kind && p.id === s.id)) continue;
      s.mesh.getWorldPosition(v).project(cam);
      if (v.z > 1) continue;
      const sx = r.left + ((v.x + 1) / 2) * r.width, sy = r.top + ((1 - v.y) / 2) * r.height;
      if (sx >= x0 && sx <= x1 && sy >= y0 && sy <= y1) picked.push(s);
    }
    if (picked.length === 0) { if (!additive) this.clearSelection(); return; }
    this.setSelection(picked, picked[picked.length - 1]);
    this.flash(`${picked.length} selected — move/rotate/scale act on the group (one undo step)`);
  }
  private onContext = (e: Event): void => { if (this.active) e.preventDefault(); };

  // ─── per-frame ───────────────────────────────────────────────────────────

  private step(dt: number): void {
    if (!this.active) return;
    const body = atlas.localMesh;
    if (!body) return;
    // Fly the body (the admin module's loop, lifted): WASD along the look, Space/Ctrl vertical.
    const yaw = atlas.localYaw;
    body.rotation.y = yaw;
    const cam = body.children[0] as THREE.Object3D | undefined;
    const pitch = cam ? cam.rotation.x : 0;
    const cp = Math.cos(pitch);
    this.fwd.set(-cp * Math.sin(yaw), Math.sin(pitch), -cp * Math.cos(yaw));
    this.right.set(Math.cos(yaw), 0, -Math.sin(yaw));
    this.move.set(0, 0, 0);
    const k = this.keys;
    const ae = document.activeElement as HTMLElement | null;
    const typing = !!ae && (ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA' || ae.isContentEditable || !!ae.closest('.cm-editor'));
    // ── GAMEPAD (2026-09-16). The sticks FLY, the d-pad drives the gizmo. An
    //    editor wants discrete, repeatable nudges far more than an analog shove,
    //    and keeping them on different sticks means the two never fight. ──
    if (!typing) this.padEdit(dt);
    if (!typing) {
      if (k.has('KeyW')) this.move.add(this.fwd);
      if (k.has('KeyS')) this.move.sub(this.fwd);
      if (k.has('KeyD')) this.move.add(this.right);
      if (k.has('KeyA')) this.move.sub(this.right);
      if (k.has('Space')) this.move.add(this.up);
      if (k.has('ControlLeft') || k.has('KeyC')) this.move.sub(this.up);
    }
    if (this.move.lengthSq() > 0) {
      this.move.normalize();
      body.position.addScaledVector(this.move, (k.has('ShiftLeft') || k.has('ShiftRight') ? FLY_BOOST : FLY_SPEED) * dt);
    }
    // Tool cursors.
    if (this.tool === 'sculpt' || this.tool === 'paint') {
      const hit = this.groundUnderCursor();
      if (hit) {
        this.showBrush(hit);
        if (this.lmbDown) {
          this.strokeAcc += dt;
          if (this.strokeAcc >= 1 / STROKE_HZ) { this.strokeAcc = 0; void this.stroke(hit); }
        }
      } else this.hideBrush();
    } else if (this.ghost) {
      const hit = this.groundUnderCursor();
      if (hit) { this.ghost.visible = true; this.ghost.position.copy(hit); this.ghost.rotation.y = this.ghostYaw; }
      else this.ghost.visible = false;
    }
    for (const b of this.selBoxes) b.update();
    if ((++this.zoneSyncCountdown & 7) === 0) this.syncZoneMarkers(); // markers for every tool (selectable), cheap cadence
  }

  /** Where the cursor ray meets the rendered ground (world-aware sampler, 1 m march). */
  private groundUnderCursor(): THREE.Vector3 | null {
    const conn = atlas.conn;
    const body = atlas.localMesh;
    const cam = body?.children[0] as THREE.PerspectiveCamera | undefined;
    if (!conn || !cam || !this.canvas) return null;
    const r = this.canvas.getBoundingClientRect();
    this.ndc.set(((this.cursorX - r.left) / r.width) * 2 - 1, -((this.cursorY - r.top) / r.height) * 2 + 1);
    this.ray.setFromCamera(this.ndc, cam);
    const o = this.ray.ray.origin, d = this.ray.ray.direction;
    const p = new THREE.Vector3();
    let prevAbove = true;
    for (let i = 1; i <= 600; i++) {
      p.copy(o).addScaledVector(d, i);
      const g = groundYIn(conn, atlas.currentWorldId, p.x, p.z);
      const above = p.y > g;
      if (!above && prevAbove) { p.y = g; return p; }
      prevAbove = above;
    }
    return null;
  }

  // ─── selection + gizmo ───────────────────────────────────────────────────

  private pick(e: PointerEvent, additive = false): void {
    const body = atlas.localMesh;
    const cam = body?.children[0] as THREE.PerspectiveCamera | undefined;
    if (!cam || !this.canvas) return;
    const r = this.canvas.getBoundingClientRect();
    this.ndc.set(((e.clientX - r.left) / r.width) * 2 - 1, -((e.clientY - r.top) / r.height) * 2 + 1);
    this.ray.setFromCamera(this.ndc, cam);
    const roots: THREE.Object3D[] = [];
    if (this.isFreeform()) roots.push(this.freeform.root);
    roots.push(this.admin.placementRoot, this.npcs.root, this.npcs.versonaMarkerRoot, this.zoneRoot, this.devices.root);
    const hits = this.ray.intersectObjects(roots, true);
    for (const h of hits) {
      let o: THREE.Object3D | null = h.object;
      while (o) {
        const pid = o.userData.ffPartId as string | undefined;
        const plid = o.userData.placementId as bigint | undefined;
        const nid = o.userData.npcId as string | undefined;
        const vid = o.userData.versonaId as string | undefined;
        const zid = o.userData.zoneId as string | undefined;
        const did = o.userData.deviceId as string | undefined;
        const hit: Selection | null = did !== undefined ? { kind: 'device', id: did, mesh: o }
          : pid !== undefined ? { kind: 'part', id: pid, mesh: o }
          : plid !== undefined ? { kind: 'placement', id: plid.toString(), mesh: o }
          : nid !== undefined ? { kind: 'npc', id: nid, mesh: o }
          : vid !== undefined ? { kind: 'versona', id: vid, mesh: o }
          : zid !== undefined ? { kind: 'zone', id: zid, mesh: o } : null;
        if (hit) { this.select(hit, additive); return; }
        o = o.parent;
      }
    }
    if (!additive) this.clearSelection();
  }

  /** Select one object; `additive` (shift) toggles it in the multi-selection. */
  private select(s: Selection, additive = false): void {
    if (!additive) { this.setSelection([s], s); return; }
    const i = this.sels.findIndex((x) => x.kind === s.kind && x.id === s.id);
    const next = [...this.sels];
    if (i >= 0) next.splice(i, 1); else next.push(s);
    if (next.length === 0) { this.clearSelection(); return; }
    this.setSelection(next, i >= 0 ? next[next.length - 1] : s);
  }

  /** THE selection entry: members + primary; 1 ⇒ gizmo on the mesh, N ⇒ gizmo on a centroid pivot. */
  private setSelection(members: Selection[], primary: Selection): void {
    this.clearSelection(false);
    this.sels = members;
    this.sel = members.find((m) => m.kind === primary.kind && m.id === primary.id) ?? members[members.length - 1];
    if (!this.scene) return;
    for (const m of members) { const b = new THREE.BoxHelper(m.mesh, m === this.sel ? 0x7ef0a0 : 0x5fb0d0); this.scene.add(b); this.selBoxes.push(b); }
    if (members.length > 1) {
      const c = new THREE.Vector3(), v = new THREE.Vector3();
      for (const m of members) c.add(m.mesh.getWorldPosition(v));
      c.divideScalar(members.length);
      if (!this.pivot) { this.pivot = new THREE.Object3D(); this.scene.add(this.pivot); }
      this.pivot.position.copy(c); this.pivot.quaternion.identity(); this.pivot.scale.set(1, 1, 1);
    }
    if (this.tool === 'move' || this.tool === 'rotate' || this.tool === 'scale') this.attachGizmo();
    atlas.gizmoHeldPartId = this.sel.kind === 'part' ? this.sel.id : null;
    // The people host leaves held units/markers to the gizmo.
    this.npcs.heldNpcId = this.sel.kind === 'npc' ? this.sel.id : this.sel.kind === 'versona' ? `versona:${this.sel.id}` : null;
    this.devices.heldDeviceId = members.some((m) => m.kind === 'device') ? (this.sel.kind === 'device' ? this.sel.id : members.find((m) => m.kind === 'device')!.id) : null;
    if (members.length === 1) {
      const s = this.sel;
      if (s.kind === 'device' && !['move', 'rotate', 'scale', 'select'].includes(this.tool)) this.setTool('devices');
      if (s.kind === 'npc' && !['move', 'rotate', 'scale', 'select'].includes(this.tool)) this.setTool('npc');
      if (s.kind === 'versona' && this.tool !== 'move' && this.tool !== 'select') this.setTool('versona');
      if (s.kind === 'zone' && !['move', 'scale', 'select'].includes(this.tool)) this.setTool('audio');
    }
    this.renderPanel();
  }

  private clearSelection(render = true): void {
    if (this.gizmo) this.gizmo.detach();
    if (this.scene) for (const b of this.selBoxes) { this.scene.remove(b); b.dispose(); }
    this.selBoxes = [];
    if (this.pivot && this.scene) { this.scene.remove(this.pivot); this.pivot = null; }
    this.pivotStart = null; this.multiBefore = null;
    atlas.gizmoHeldPartId = null;
    this.npcs.heldNpcId = null;
    this.devices.heldDeviceId = null;
    this.sel = null;
    this.sels = [];
    if (render && this.active) this.renderPanel();
  }

  private ensureGizmo(): void {
    if (this.gizmo || !this.scene || !this.canvas) return;
    const body = atlas.localMesh;
    if (!body) return;
    const cam = body.children[0] as unknown as import('three').Camera;
    const gizmo = new TransformControls(cam, this.canvas);
    gizmo.addEventListener('dragging-changed', (e) => {
      if (e.value) {
        if (!this.sel) return;
        if (this.sels.length > 1 && this.pivot) {
          // Grouped transaction: every member's pose + row, and the pivot's start.
          this.multiBefore = this.sels.map((m) => ({
            s: m, pos: m.mesh.position.clone(), quat: m.mesh.quaternion.clone(), scale: m.mesh.scale.clone(), xf: xfOf(m.mesh),
            snap: m.kind === 'part' ? this.partSnapshot(m.id) : null, npc: m.kind === 'npc' ? this.npcRow(m.id) : null, zone: m.kind === 'zone' ? this.zoneRow(m.id) : null, dev: m.kind === 'device' ? this.devices.rowOf(m.id) : null,
          }));
          this.pivotStart = { pos: this.pivot.position.clone(), quat: this.pivot.quaternion.clone(), scale: this.pivot.scale.clone() };
          return;
        }
        this.dragBefore = xfOf(this.sel.mesh);
        this.dragPartBefore = this.sel.kind === 'part' ? this.partSnapshot(this.sel.id) : null;
      } else void this.commitDrag();
    });
    gizmo.addEventListener('objectChange', () => { if (this.sels.length > 1) this.applyPivotDelta(); this.refreshInspector(); });
    const helper = gizmo.getHelper();
    this.scene.add(helper);
    this.gizmo = gizmo;
    this.gizmoHelper = helper;
    this.applySnap();
  }

  private attachGizmo(): void {
    this.ensureGizmo();
    if (!this.gizmo || !this.sel) return;
    this.gizmo.setMode(this.tool === 'rotate' ? 'rotate' : this.tool === 'scale' ? 'scale' : 'translate');
    this.gizmo.attach(this.sels.length > 1 && this.pivot ? this.pivot : this.sel.mesh);
  }

  /** Live during a multi-drag: the pivot's delta applied to every member (offset rotated + scaled about the pivot). */
  private applyPivotDelta(): void {
    const pv = this.pivot, start = this.pivotStart, before = this.multiBefore;
    if (!pv || !start || !before) return;
    const dq = pv.quaternion.clone().multiply(start.quat.clone().invert());
    const ds = pv.scale.x / Math.max(1e-6, start.scale.x);
    const off = new THREE.Vector3();
    for (const b of before) {
      off.copy(b.pos).sub(start.pos).multiplyScalar(ds).applyQuaternion(dq);
      b.s.mesh.position.copy(pv.position).add(off);
      if (b.s.kind !== 'versona') b.s.mesh.quaternion.copy(dq).multiply(b.quat);
      if (b.s.kind !== 'versona') b.s.mesh.scale.copy(b.scale).multiplyScalar(ds);
    }
  }

  private zoneRow(id: string): ZoneRow | null {
    const conn = atlas.conn;
    if (!conn) return null;
    try { return (conn.db.audioZone.zoneId.find(BigInt(id)) as ZoneRow | undefined) ?? null; } catch { return null; }
  }

  /** The command that moves ONE member from `before` to its mesh's current pose (null = nothing to do). */
  private memberCmd(s: Selection, before: XF, snap: PartRowSnapshot | null, npc: NpcRowLite | null, zone: ZoneRow | null, dev: DeviceRow | null = null): BuildCommand | null {
    const after = xfOf(s.mesh);
    const yawOf = (q: XF): number => new THREE.Euler().setFromQuaternion(new THREE.Quaternion(q.rotX, q.rotY, q.rotZ, q.rotW), 'YXZ').y;
    switch (s.kind) {
      case 'part': { if (!snap) return null; return new EditPartCmd(`Move ${snap.primType}`, snap, { ...snap, ...after }); }
      case 'placement': return new PlacementXfCmd('Transform placement', BigInt(s.id), before, after);
      case 'npc': {
        if (!npc) return null;
        const clampS = (v: number): number => Math.min(NPC_SCALE_MAX, Math.max(NPC_SCALE_MIN, v));
        return new NpcXfCmd(npc, { posX: before.posX, posY: before.posY, posZ: before.posZ, yaw: yawOf(before), scale: clampS(before.scaleX) },
          { posX: after.posX, posY: after.posY, posZ: after.posZ, yaw: yawOf(after), scale: clampS(after.scaleX) });
      }
      case 'versona': return new VersonaMoveCmd(BigInt(s.id), { x: before.posX, z: before.posZ }, { x: after.posX, z: after.posZ });
      case 'zone': {
        if (!zone) return null;
        const radius = Math.min(500, Math.max(0.5, Math.abs(after.scaleX)));
        return new ZoneUpsertCmd(`Edit zone ${zone.name}`, zone, { ...zone, cx: after.posX, cy: after.posY, cz: after.posZ, radius });
      }
      case 'device': {
        const row = dev ?? this.devices.rowOf(s.id);
        if (!row) return null;
        // The Scale gizmo scales the device's SIZE fields (the row rebuilds at the new extent); the mesh scale resets.
        const kx = before.scaleX ? after.scaleX / before.scaleX : 1, ky = before.scaleY ? after.scaleY / before.scaleY : 1, kz = before.scaleZ ? after.scaleZ / before.scaleZ : 1;
        s.mesh.scale.set(1, 1, 1);
        const b: DeviceXf = { posX: before.posX, posY: before.posY, posZ: before.posZ, yaw: yawOf(before), sx: row.sx, sy: row.sy, sz: row.sz };
        const a: DeviceXf = { posX: after.posX, posY: after.posY, posZ: after.posZ, yaw: yawOf(after), sx: Math.max(0.05, row.sx * kx), sy: Math.max(0.05, row.sy * ky), sz: Math.max(0.05, row.sz * kz) };
        return new DeviceXfCmd(row, b, a);
      }
    }
  }

  private applySnap(): void {
    if (!this.gizmo) return;
    this.gizmo.setTranslationSnap(this.snap ? 1 : null);
    this.gizmo.setRotationSnap(this.snap ? THREE.MathUtils.degToRad(15) : null);
    this.gizmo.setScaleSnap(this.snap ? 0.25 : null);
  }

  private partSnapshot(id: string): PartRowSnapshot | null {
    const conn = atlas.conn;
    if (!conn) return null;
    try { for (const p of conn.db.part.iter()) if (p.partId.toString() === id) return snapshotPart(p); } catch { /* churn */ }
    return null;
  }

  /**
   * The pad in Edit mode: the left stick flies, the triggers lift and drop, and
   * the d-pad nudges the SELECTION through the very same before/after bracket a
   * mouse drag uses — so a pad edit lands on the undo stack and the network as
   * one indistinguishable step. Nudges are gathered into ONE transaction and
   * committed once the d-pad has been quiet for a moment, so holding a direction
   * is a single undo, not thirty.
   */
  private padEdit(dt: number): void {
    const snap = padSnap();
    if (!snap.present) { if (this.padNudging) void this.padCommitNudge(); return; }

    // Fly: the left stick along the look, the triggers vertical.
    if (Math.abs(snap.lx) > 0.01 || Math.abs(snap.ly) > 0.01) {
      this.move.addScaledVector(this.fwd, -snap.ly);
      this.move.addScaledVector(this.right, snap.lx);
    }
    const lift = snap.rt - snap.lt;
    if (Math.abs(lift) > 0.01) this.move.addScaledVector(this.up, lift);

    // The tool ring, and dropping the selection.
    if (padPressed('editToolNext') || padPressed('editToolPrev')) {
      const ring: Tool[] = ['move', 'rotate', 'scale'];
      const at = ring.indexOf(this.tool as Tool);
      const step = padPressed('editToolNext') ? 1 : -1;
      const next = ring[((at < 0 ? 0 : at) + step + ring.length) % ring.length];
      this.setTool(next);
      this.flash(`${next} — d-pad nudges, ${this.snap ? 'snapped' : 'free'}`);
      padRumble('ui');
      return;
    }
    if (padPressed('editDeselect')) { void this.padCommitNudge(); this.clearSelection(); return; }
    if (padPressed('editCommit')) { void this.padCommitNudge(); return; }

    if (!this.sel) { if (this.padNudging) void this.padCommitNudge(); return; }

    // The nudge itself, repeating while held.
    const nx = (padRepeat('editNudgeRight', dt) ? 1 : 0) - (padRepeat('editNudgeLeft', dt) ? 1 : 0);
    const nz = (padRepeat('editNudgeDown', dt) ? 1 : 0) - (padRepeat('editNudgeUp', dt) ? 1 : 0);
    if (nx === 0 && nz === 0) {
      // Quiet: commit once the hold has really ended, not between repeats.
      if (this.padNudging) {
        this.padQuietS += dt;
        if (this.padQuietS > PAD_NUDGE_COMMIT_S) void this.padCommitNudge();
      }
      return;
    }
    this.padQuietS = 0;
    if (!this.padNudging) this.padBeginNudge();

    const target = this.sels.length > 1 && this.pivot ? this.pivot : this.sel.mesh;
    const tool: NudgeTool = this.tool === 'rotate' ? 'rotate' : this.tool === 'scale' ? 'scale' : 'move';
    const n = editNudge(tool, nx, -nz, atlas.localYaw, this.snap);   // d-pad UP is away from the camera
    if (n.dYaw) target.rotateY(n.dYaw);
    if (n.dPitch) target.rotateX(n.dPitch);
    if (n.scale !== 1) target.scale.multiplyScalar(n.scale);
    target.position.x += n.dx;
    target.position.z += n.dz;
    if (this.sels.length > 1) this.applyPivotDelta();
    this.refreshInspector();
    padRumble('ui');
  }

  /** Open the same transaction a mouse drag opens. */
  private padBeginNudge(): void {
    if (!this.sel) return;
    this.padNudging = true;
    this.padQuietS = 0;
    if (this.sels.length > 1 && this.pivot) {
      this.multiBefore = this.sels.map((m) => ({
        s: m, pos: m.mesh.position.clone(), quat: m.mesh.quaternion.clone(), scale: m.mesh.scale.clone(), xf: xfOf(m.mesh),
        snap: m.kind === 'part' ? this.partSnapshot(m.id) : null, npc: m.kind === 'npc' ? this.npcRow(m.id) : null, zone: m.kind === 'zone' ? this.zoneRow(m.id) : null, dev: m.kind === 'device' ? this.devices.rowOf(m.id) : null,
      }));
      this.pivotStart = { pos: this.pivot.position.clone(), quat: this.pivot.quaternion.clone(), scale: this.pivot.scale.clone() };
      return;
    }
    this.dragBefore = xfOf(this.sel.mesh);
    this.dragPartBefore = this.sel.kind === 'part' ? this.partSnapshot(this.sel.id) : null;
  }

  /** …and close it through the same commit, so the undo stack cannot tell the
   *  difference between a pad nudge and a mouse drag. */
  private async padCommitNudge(): Promise<void> {
    if (!this.padNudging) return;
    this.padNudging = false;
    this.padQuietS = 0;
    await this.commitDrag();
  }
  private padNudging = false;
  private padQuietS = 0;

  private async commitDrag(): Promise<void> {
    if (this.multiBefore) {
      const before = this.multiBefore;
      this.multiBefore = null; this.pivotStart = null;
      const cmds = before.map((b) => this.memberCmd(b.s, b.xf, b.snap, b.npc, b.zone, b.dev)).filter((c): c is BuildCommand => c !== null);
      if (cmds.length === 0) return;
      try { await this.stack.run(new GroupCmd(`Move ${cmds.length} objects`, cmds)); }
      catch (e) { this.flash(`Group edit refused: ${(e as Error).message}`); }
      this.renderPanel();
      return;
    }
    const s = this.sel;
    if (!s || !this.dragBefore) return;
    const before = this.dragBefore;
    this.dragBefore = null;
    const snap = this.dragPartBefore;
    this.dragPartBefore = null;
    try {
      const cmd = this.memberCmd(s, before, snap, s.kind === 'npc' ? this.npcRow(s.id) : null, s.kind === 'zone' ? this.zoneRow(s.id) : null, s.kind === 'device' ? this.devices.rowOf(s.id) : null);
      if (cmd) await this.stack.run(cmd);
    } catch (e) { this.flash(`Edit refused: ${(e as Error).message}`); }
    this.renderPanel();
  }

  /** The delete command for one member (versonas are confirmed + direct — not undoable). */
  private async deleteCmdFor(s: Selection, conn: NonNullable<typeof atlas.conn>): Promise<BuildCommand | null> {
    switch (s.kind) {
      case 'part': { const snap = this.partSnapshot(s.id); return snap ? new DeletePartCmd(snap) : null; }
      case 'placement': { const row = this.admin.placementRow(s.id); return row ? new PlacementDeleteCmd({ ...row }) : null; }
      case 'npc': {
        const row = this.npcRow(s.id); const full = conn.db.npcUnit.npcId.find(BigInt(s.id));
        return row && full ? new NpcDeleteCmd({ ...row, posX: full.posX, posY: full.posY, posZ: full.posZ, yaw: full.yaw, scale: full.scale }) : null;
      }
      case 'zone': { const z = this.zoneRow(s.id); return z ? new ZoneDeleteCmd(z) : null; }
      case 'device': { const r = this.devices.rowOf(s.id); return r ? new DeviceDeleteCmd(r) : null; }
      case 'versona': return null;
    }
  }

  private async deleteSelected(): Promise<void> {
    const conn = atlas.conn;
    if (this.sels.length === 0 || !conn) return;
    const members = [...this.sels];
    const versonas = members.filter((m) => m.kind === 'versona');
    if (versonas.length > 0 && !window.confirm(`Remove ${versonas.length} versona(s)? (their chat and memories go with them — no undo)`)) return;
    try {
      const cmds: BuildCommand[] = [];
      for (const m of members) { const c = await this.deleteCmdFor(m, conn); if (c) cmds.push(c); }
      this.clearSelection();
      for (const v of versonas) await conn.reducers.deleteVersona({ versonaId: BigInt(v.id) });
      if (cmds.length === 1) await this.stack.run(cmds[0]);
      else if (cmds.length > 1) await this.stack.run(new GroupCmd(`Delete ${cmds.length} objects`, cmds));
      this.flash(cmds.length ? `Deleted ${cmds.length + versonas.length} (Ctrl+Z undoes ${cmds.length})` : 'Deleted');
    } catch (e) { this.flash(`Delete refused: ${(e as Error).message}`); }
    this.renderPanel();
  }

  /** The duplicate command for one member, offset +2 m in X (versonas/zones are placed again via their tools). */
  private duplicateCmdFor(s: Selection, conn: NonNullable<typeof atlas.conn>, wid: bigint): BuildCommand | null {
    const xf = xfOf(s.mesh); xf.posX += 2;
    switch (s.kind) {
      case 'part': {
        const snap = this.partSnapshot(s.id); if (!snap) return null;
        const args = { worldId: wid, ...xf, shapeJson: snap.shapeJson, materialJson: snap.materialJson, physics: snap.physics };
        return snap.primType === 'mesh' ? new RezCmd(`Duplicate ${snap.prefabKey}`, { ...args, prefabKey: snap.prefabKey }, true) : new RezCmd(`Duplicate ${snap.primType}`, { ...args, primType: snap.primType }, false);
      }
      case 'placement': {
        const row = this.admin.placementRow(s.id); if (!row) return null;
        return { label: `Duplicate ${row.prefabKey}`, execute: () => conn.reducers.adminPlaceObject({ worldId: wid, prefabKey: row.prefabKey, kind: row.kind, ...xf }), undo: async () => { /* placement id unknown — not undoable */ } };
      }
      case 'npc': {
        const full = conn.db.npcUnit.npcId.find(BigInt(s.id)); if (!full) return null;
        return { label: `Duplicate ${full.name}`, execute: () => conn.reducers.placeNpc({ worldId: wid, name: full.name, modelKey: full.modelKey, posX: xf.posX, posY: full.posY, posZ: full.posZ, yaw: full.yaw, scale: full.scale, scriptSrc: full.scriptSrc, configJson: full.configJson }), undo: async () => { /* id unknown */ } };
      }
      case 'device': {
        const r = this.devices.rowOf(s.id); if (!r) return null;
        return { label: `Duplicate ${r.kind}`, execute: () => conn.reducers.placeDevice({ worldId: wid, kind: r.kind, name: `${r.name} copy`, posX: xf.posX, posY: r.posY, posZ: r.posZ, yaw: r.yaw, sx: r.sx, sy: r.sy, sz: r.sz, configJson: r.configJson }), undo: async () => { /* id unknown */ } };
      }
      default: return null;
    }
  }

  private async duplicateSelected(): Promise<void> {
    const conn = atlas.conn;
    const wid = atlas.currentWorldId;
    if (this.sels.length === 0 || !conn || wid === null) return;
    try {
      const cmds = this.sels.map((m) => this.duplicateCmdFor(m, conn, wid)).filter((c): c is BuildCommand => c !== null);
      if (cmds.length === 0) { this.flash('Place another versona/zone with its tool'); return; }
      if (cmds.length === 1) await this.stack.run(cmds[0]); else await this.stack.run(new GroupCmd(`Duplicate ${cmds.length} objects`, cmds));
      this.flash(`Duplicated ${cmds.length}`);
    } catch (e) { this.flash(`Duplicate refused: ${(e as Error).message}`); }
  }

  /** Snap every selected object's base to the ground under it (one undo step). */
  private async dropToFloor(): Promise<void> {
    const conn = atlas.conn;
    if (!conn || this.sels.length === 0) return;
    const cmds: BuildCommand[] = [];
    for (const m of this.sels) {
      if (m.kind === 'versona') continue;
      const before = xfOf(m.mesh);
      const box = new THREE.Box3().setFromObject(m.mesh);
      const g = groundYIn(conn, atlas.currentWorldId, m.mesh.position.x, m.mesh.position.z);
      const dy = g - box.min.y;
      if (Math.abs(dy) < 1e-3) continue;
      m.mesh.position.y += dy;
      const c = this.memberCmd(m, before, m.kind === 'part' ? this.partSnapshot(m.id) : null, m.kind === 'npc' ? this.npcRow(m.id) : null, m.kind === 'zone' ? this.zoneRow(m.id) : null, m.kind === 'device' ? this.devices.rowOf(m.id) : null);
      if (c) cmds.push(c); else m.mesh.position.y -= dy;
    }
    if (cmds.length === 0) { this.flash('Already on the ground'); return; }
    try { await this.stack.run(cmds.length === 1 ? cmds[0] : new GroupCmd(`Drop ${cmds.length} to floor`, cmds)); this.flash(`Dropped ${cmds.length} to the floor`); }
    catch (e) { this.flash(`Drop refused: ${(e as Error).message}`); }
    this.renderPanel();
  }

  /** Save the selected build parts as a reusable prefab (this device's library). */
  private savePrefabFromSelection(): void {
    const snaps = this.sels.filter((m) => m.kind === 'part').map((m) => this.partSnapshot(m.id)).filter((x): x is PartRowSnapshot => x !== null);
    if (snaps.length === 0) { this.flash('Prefabs are made of build parts — select some parts'); return; }
    const name = window.prompt(`Prefab name (${snaps.length} part${snaps.length === 1 ? '' : 's'})`, `prefab ${loadPrefabs().length + 1}`);
    if (!name) return;
    try {
      const def = capturePrefab(name, snaps);
      savePrefabs(upsertPrefab(loadPrefabs(), def));
      this.flash(`Saved prefab "${def.name}" — Place tool → My prefabs`);
    } catch (e) { this.flash((e as Error).message); }
    this.renderPanel();
  }

  // ─── place tool ──────────────────────────────────────────────────────────  // ─── place tool ──────────────────────────────────────────────────────────

  private setPlaceChoice(c: typeof this.placeChoice): void {
    this.placeChoice = c;
    this.clearGhost();
    if (!c || !this.scene) return;
    const g = new THREE.Group();
    const mat = new THREE.MeshStandardMaterial({ color: 0x7ef0a0, transparent: true, opacity: 0.45, depthWrite: false });
    const prim = c.kind === 'prim' ? c.key : c.kind === 'npc' || c.kind === 'versona' ? 'person' : c.kind === 'zone' ? 'zone' : 'box';
    const devDef = c.kind === 'device' ? DEVICE_BY_KIND.get(c.key) : undefined;
    if (devDef) mat.color.set(0xffb86b);
    if (c.kind === 'versona') mat.color.set(0xe07bff);
    if (c.kind === 'zone') { mat.color.set(0x66c2ff); mat.opacity = 0.22; }
    const geo = prim === 'person' ? new THREE.CapsuleGeometry(0.35, 1.1, 6, 12)
      : prim === 'zone' ? new THREE.SphereGeometry(Math.max(0.5, this.zoneDraft.radius), 20, 14)
      : c.kind === 'prefabset' && c.prefab ? new THREE.BoxGeometry(c.prefab.size.x, c.prefab.size.y, c.prefab.size.z)
      : devDef ? new THREE.BoxGeometry(Math.max(0.3, devDef.size[0]), Math.max(0.3, devDef.size[1]), Math.max(0.3, devDef.size[2]))
      : prim === 'sphere' ? new THREE.SphereGeometry(0.5, 16, 12)
      : prim === 'cylinder' ? new THREE.CylinderGeometry(0.5, 0.5, 1, 16)
      : prim === 'cone' ? new THREE.ConeGeometry(0.5, 1, 16)
      : prim === 'torus' ? new THREE.TorusGeometry(0.5, 0.18, 10, 24)
      : new THREE.BoxGeometry(c.kind === 'prim' ? 1 : 3, c.kind === 'prim' ? 1 : 3, c.kind === 'prim' ? 1 : 3);
    const m = new THREE.Mesh(geo, mat);
    m.position.y = c.kind === 'prim' ? 0.5 : prim === 'person' ? 0.9 : prim === 'zone' ? 1 : c.kind === 'prefabset' && c.prefab ? c.prefab.size.y / 2 : devDef ? Math.max(0.3, devDef.size[1]) / 2 : 1.5;
    g.add(m);
    this.scene.add(g);
    this.ghost = g;
    // Structures arrive at a REAL size (lib/structure_pivot `fitScaleFor`):
    // deed types use the deployed-structure scale map immediately; every
    // structure is then measured and fitted (civ 14 m, legacy 10/20 m by
    // class, footprint-capped) and the ghost becomes its true footprint.
    if (c.kind === 'prefab' && c.key.startsWith('structure:')) {
      const deed = TYPE_SCALE[c.key.slice('structure:'.length)];
      if (deed) { c.scale = deed; this.sizeGhost(g, deed); }
      void this.fitStructure(c, g, deed);
    }
    this.flash(c.kind === 'npc' ? `Click the ground to place NPC "${this.npcDraft.name || 'Unnamed'}" · R rotates (facing) · Esc cancels`
      : c.kind === 'versona' ? 'Click the ground to place the versona · Esc cancels'
      : c.kind === 'zone' ? `Click the ground to place the ${this.zoneDraft.kind} zone (${this.zoneDraft.radius} m) · Esc cancels`
      : c.kind === 'prefabset' ? `Click the ground to place prefab "${c.key}" · R rotates · Esc cancels`
      : c.kind === 'device' ? `Click the ground to place a ${DEVICE_BY_KIND.get(c.key)?.label ?? c.key} · R rotates · Esc cancels`
      : `Click the ground to place ${c.key.replace(/^[a-z]+:/, '')} · R rotates · Esc cancels`);
  }

  private npcRow(id: string): NpcRowLite | null {
    const conn = atlas.conn;
    if (!conn) return null;
    try { const r = conn.db.npcUnit.npcId.find(BigInt(id)); return r ? { npcId: r.npcId, worldId: r.worldId, name: r.name, modelKey: r.modelKey, scriptSrc: r.scriptSrc, enabled: r.enabled, configJson: r.configJson } : null; } catch { return null; }
  }

  private sizeGhost(g: THREE.Object3D, scale: number): void {
    const box = g.children[0];
    if (box) { box.scale.setScalar(Math.max(0.2, scale * 0.63)); box.position.y = (scale * 1.9) / 2; }
  }

  /** Measure the structure GLB once: the fit scale (lib/structure_pivot `fitScaleFor`) and a footprint-true ghost. */
  private async fitStructure(c: NonNullable<typeof this.placeChoice>, g: THREE.Object3D, deedScale?: number): Promise<void> {
    const conn = atlas.conn;
    if (!conn) return;
    const id = prefabAssetId(conn, c.key);
    if (id === null) return;
    try {
      const obj = await this.assets.load(id);
      if (this.placeChoice !== c) { this.assets.dispose(id); return; }
      if (this.measuredAsset !== null) this.assets.dispose(this.measuredAsset);
      this.measuredAsset = id;
      const bb = new THREE.Box3().setFromObject(obj);
      const size = { x: bb.max.x - bb.min.x, y: bb.max.y - bb.min.y, z: bb.max.z - bb.min.z };
      const sc = fitScaleFor(c.key, size, deedScale);
      c.scale = sc;
      // The ghost shows the REAL footprint (seated bottom-center, like the placed part). Capped for the far-flung-vertex landmarks.
      const box = g.children[0];
      if (box) {
        const w = Math.min(120, Math.max(0.3, size.x * sc)), h = Math.min(120, Math.max(0.3, size.y * sc)), d = Math.min(120, Math.max(0.3, size.z * sc));
        box.scale.set(w / 3, h / 3, d / 3); box.position.y = h / 2;
      }
    } catch { /* keep the map / 1× scale — the Scale tool can fix it after placement */ }
  }

  private clearGhost(): void {
    if (this.measuredAsset !== null) { this.assets.dispose(this.measuredAsset); this.measuredAsset = null; }
    if (this.ghost && this.scene) {
      this.scene.remove(this.ghost);
      this.ghost.traverse((o) => { const mm = o as THREE.Mesh; if (mm.isMesh) { mm.geometry.dispose(); (mm.material as THREE.Material).dispose(); } });
    }
    this.ghost = null;
  }

  /** A place call is on its way to the server (the in-flight latch below). */
  private placing = false;

  /** ONE PLACE IN FLIGHT (2026-09-24): the ghost stays put after a click (to place many), so a double click — or a
   *  second click while the model was still streaming in — asked for the same object on the same spot. The server
   *  now refuses an identical object there (adminPlaceObject); this keeps the second click from asking at all. */
  private async placeAtGhost(): Promise<void> {
    if (this.placing) return;
    this.placing = true;
    try { await this.placeAtGhostOnce(); } finally { this.placing = false; }
  }

  private async placeAtGhostOnce(): Promise<void> {
    const c = this.placeChoice;
    const conn = atlas.conn;
    const wid = atlas.currentWorldId;
    if (!c || !this.ghost || !conn || wid === null) return;
    const p = this.ghost.position;
    const q = new THREE.Quaternion().setFromEuler(new THREE.Euler(0, this.ghostYaw, 0));
    const sc = c.scale || 1;
    const xf = { posX: p.x, posY: p.y, posZ: p.z, rotX: q.x, rotY: q.y, rotZ: q.z, rotW: q.w, scaleX: sc, scaleY: sc, scaleZ: sc };
    try {
      if (c.kind === 'npc') {
        const d = this.npcDraft;
        const preset = NPC_PRESETS.find((x) => x.key === d.preset);
        await conn.reducers.placeNpc({ worldId: wid, name: d.name.trim() || 'Unnamed', modelKey: d.modelKey, posX: p.x, posY: p.y, posZ: p.z, yaw: this.ghostYaw, scale: 1, scriptSrc: npcTemplate(d.template), configJson: JSON.stringify(preset?.config ?? {}) });
        this.flash(`NPC "${d.name.trim() || 'Unnamed'}" placed — select it to edit its script`);
        this.clearGhost(); this.placeChoice = null;
        this.renderPanel();
        return;
      }
      if (c.kind === 'prefabset' && c.prefab) {
        if (this.isCommons()) { this.flash('Prefabs rez build parts — place them in personal/new worlds'); return; }
        const specs = instantiatePrefab(c.prefab, wid, { x: p.x, y: p.y, z: p.z }, this.ghostYaw);
        const cmds = specs.map((sp, i) => new RezCmd(`Prefab part ${i + 1}`, sp.args, sp.catalog));
        await this.stack.run(new GroupCmd(`Place prefab ${c.prefab.name} (${cmds.length})`, cmds));
        this.flash(`Placed "${c.prefab.name}" — ${cmds.length} parts (one undo step)`);
        this.renderPanel();
        return;
      }
      if (c.kind === 'device') {
        const def = DEVICE_BY_KIND.get(c.key);
        if (!def) return;
        let n = 1; for (const id of this.devices.ids()) if (this.devices.rowOf(id)?.kind === c.key) n++;
        const name = `${c.key}_${n}`;
        const cfg = { ...def.defaults } as Record<string, unknown>;
        if (c.key === 'teleporter') { cfg.tx = Math.round(p.x + 6); cfg.ty = Math.round(p.y); cfg.tz = Math.round(p.z); }
        await conn.reducers.placeDevice({ worldId: wid, kind: c.key, name, posX: p.x, posY: p.y, posZ: p.z, yaw: this.ghostYaw, sx: def.size[0], sy: def.size[1], sz: def.size[2], configJson: JSON.stringify(cfg) });
        this.flash(`${def.label} "${name}" placed — select it to configure`);
        if (!this.keys.has('ShiftLeft')) { this.clearGhost(); this.placeChoice = null; }
        this.renderPanel();
        return;
      }
      if (c.kind === 'zone') {
        const d = this.zoneDraft;
        if (d.assetId === 0n) { this.flash('Pick an audio clip first'); return; }
        const name = `zone_${Date.now().toString(36)}`;
        const row: ZoneRow = { zoneId: 0n, worldId: wid, name, audioAssetId: d.assetId, kind: d.kind, shapeType: 'sphere', cx: p.x, cy: p.y + 1, cz: p.z, radius: d.radius, sx: 0, sy: 0, sz: 0, gain: d.gain, loop: d.loop, fadeDuration: d.fade, priority: 0 };
        await this.stack.run(new ZoneUpsertCmd(`Place ${d.kind} zone`, null, row));
        this.flash(`${d.kind} zone placed (${d.radius} m) — select it to move / resize`);
        this.clearGhost(); this.placeChoice = null;
        this.renderPanel();
        return;
      }
      if (c.kind === 'versona') {
        const d = this.versonaDraft;
        await conn.reducers.placeVersona({
          worldId: wid, x: p.x, z: p.z, name: d.name.trim(), archetype: d.archetype, persona: d.persona, context: d.context,
          greeting: d.greeting, deflect: d.deflect, topicsJson: topicsTextToJson(d.topicsText),
          llm: d.llm, strict: d.strict, temperature: d.temperature, maxWords: d.maxWords, wanderM: d.wanderM,
        });
        this.flash(this.isCommons() ? 'Versona placed — it joins the nearest settlement within a minute' : `Versona "${d.name.trim() || 'unnamed'}" placed`);
        this.clearGhost(); this.placeChoice = null;
        this.renderPanel();
        return;
      }
      if (this.isCommons()) {
        if (c.kind === 'prefab') await conn.reducers.adminPlaceObject({ worldId: wid, prefabKey: c.key, kind: c.key.startsWith('structure:') ? 'structure' : 'prop', ...xf });
        else this.flash('On the Commons place catalog prefabs (uploads become prefabs via the asset pipeline)');
      } else if (c.kind === 'prim') {
        await this.stack.run(new RezCmd(`Place ${c.key}`, { worldId: wid, primType: c.key, ...xf, posY: p.y + 0.5, shapeJson: '{}', materialJson: '{}', physics: true }, false));
      } else if (c.kind === 'prefab') {
        await this.stack.run(new RezCmd(`Place ${c.key}`, { worldId: wid, prefabKey: c.key, ...xf, physics: true }, true));
      } else if (c.kind === 'upload' && c.assetId !== undefined) {
        await conn.reducers.rezUploadedPart({ worldId: wid, renderAssetId: c.assetId, ...xf, physics: true });
      }
    } catch (e) { this.flash(`Place refused: ${(e as Error).message}`); }
    this.renderPanel();
  }

  // ─── sculpt / paint ──────────────────────────────────────────────────────

  private showBrush(p: THREE.Vector3): void {
    if (!this.scene) return;
    if (!this.brushRing) {
      this.brushRing = new THREE.Mesh(new THREE.RingGeometry(0.92, 1, 48), new THREE.MeshBasicMaterial({ color: 0x7ef0a0, transparent: true, opacity: 0.8, side: THREE.DoubleSide, depthTest: false }));
      this.brushRing.rotation.x = -Math.PI / 2;
      this.brushRing.renderOrder = 999;
      this.scene.add(this.brushRing);
    }
    this.brushRing.visible = true;
    this.brushRing.position.set(p.x, p.y + 0.15, p.z);
    this.brushRing.scale.setScalar(this.brushR);
    (this.brushRing.material as THREE.MeshBasicMaterial).color.set(this.tool === 'paint' ? 0xffd166 : 0x7ef0a0);
  }
  private hideBrush(): void { if (this.brushRing) this.brushRing.visible = false; }

  private async stroke(p: THREE.Vector3): Promise<void> {
    const conn = atlas.conn;
    const wid = atlas.currentWorldId;
    if (!conn || wid === null) return;
    try {
      if (this.tool === 'sculpt') {
        const lower = this.keys.has('ControlLeft') || this.keys.has('ControlRight');
        const flat = this.keys.has('AltLeft') || this.keys.has('AltRight');
        if (flat) await conn.reducers.terrainSculpt({ worldId: wid, kind: 'flatten', x: p.x, z: p.z, radius: this.brushR, value: p.y, layer: 0 });
        else await conn.reducers.terrainSculpt({ worldId: wid, kind: 'raise', x: p.x, z: p.z, radius: this.brushR, value: lower ? -this.brushStrength : this.brushStrength, layer: 0 });
      } else {
        await conn.reducers.terrainSculpt({ worldId: wid, kind: 'paint', x: p.x, z: p.z, radius: this.brushR, value: this.paintStrength, layer: this.paintLayer });
      }
    } catch (e) { this.lmbDown = false; this.flash(`Brush refused: ${(e as Error).message}`); }
  }

  // ─── DOM shell ───────────────────────────────────────────────────────────

  private buildDom(): void {
    const root = document.createElement('div');
    root.id = 'edit-mode';
    // z 39: UI windows (the Lua IDE, talk windows) start at 40 and float ABOVE the editor chrome.
    root.style.cssText = 'position:fixed;inset:0;z-index:39;pointer-events:none;font:12px/1.45 var(--ax-font);color:var(--ax-text)';
    // toolbar
    const bar = document.createElement('div');
    bar.style.cssText = 'position:absolute;top:10px;left:50%;transform:translateX(-50%);display:flex;flex-wrap:wrap;justify-content:center;max-width:96vw;gap:4px;align-items:center;'
      + 'background:color-mix(in srgb, var(--ax-bg) 92%, transparent);border:1px solid var(--ax-line);border-radius:var(--ax-win-radius);padding:6px 8px;pointer-events:auto;box-shadow:0 6px 24px rgba(0,0,0,.45)';
    const title = document.createElement('div');
    title.innerHTML = `${iconMarkup('build')}<b>EDIT</b>`;
    title.style.cssText = 'color:var(--ax-good);margin:0 8px 0 2px;display:flex;gap:4px;align-items:center';
    bar.appendChild(title);
    for (const t of TOOLS) {
      const b = document.createElement('button');
      b.innerHTML = `${iconMarkup(t.icon)}<span>${t.label}</span><kbd>${t.kbd ?? t.key.replace('Digit', '')}</kbd>`;
      b.title = t.hint;
      b.style.cssText = this.btnCss(false);
      b.onclick = () => this.setTool(t.id);
      bar.appendChild(b);
      this.toolBtns.set(t.id, b);
    }
    const sep = document.createElement('span'); sep.style.cssText = 'width:1px;height:22px;background:var(--ax-sel);margin:0 4px'; bar.appendChild(sep);
    const undo = document.createElement('button'); undo.textContent = '↶'; undo.title = 'Undo (Ctrl+Z)'; undo.style.cssText = this.btnCss(false); undo.onclick = () => void this.stack.undo().then(() => this.renderPanel()); bar.appendChild(undo);
    const redo = document.createElement('button'); redo.textContent = '↷'; redo.title = 'Redo (Ctrl+Y)'; redo.style.cssText = this.btnCss(false); redo.onclick = () => void this.stack.redo().then(() => this.renderPanel()); bar.appendChild(redo);
    const exit = document.createElement('button'); exit.innerHTML = `${iconMarkup('exit')}Exit <kbd>Esc</kbd>`; exit.style.cssText = this.btnCss(false) + 'color:var(--ax-bad)'; exit.onclick = () => this.exit(); bar.appendChild(exit);
    root.appendChild(bar);
    // context panel
    const panel = document.createElement('div');
    panel.style.cssText = 'position:absolute;top:64px;right:12px;bottom:96px;width:320px;overflow:auto;background:color-mix(in srgb, var(--ax-bg) 92%, transparent);'
      + 'border:1px solid var(--ax-line);border-radius:var(--ax-win-radius);padding:10px 12px;pointer-events:auto;box-shadow:0 6px 24px rgba(0,0,0,.45)';
    root.appendChild(panel);
    // status
    const status = document.createElement('div');
    status.style.cssText = 'position:absolute;left:50%;bottom:70px;transform:translateX(-50%);background:color-mix(in srgb, var(--ax-bg) 85%, transparent);border:1px solid var(--ax-line);'
      + 'border-radius:var(--ax-radius);padding:5px 12px;color:var(--ax-text);pointer-events:none;white-space:nowrap';
    root.appendChild(status);
    document.body.appendChild(root);
    this.root = root; this.bar = bar; this.panel = panel; this.status = status;
    this.canvas?.addEventListener('pointerdown', this.onPointerDown);
    window.addEventListener('pointerup', this.onPointerUp);
    window.addEventListener('pointermove', this.onPointerMove);
    this.canvas?.addEventListener('contextmenu', this.onContext);
  }

  private btnCss(on: boolean): string {
    return 'display:inline-flex;gap:5px;align-items:center;cursor:pointer;font:600 12px var(--ax-font);padding:5px 9px;border-radius:var(--ax-radius);'
      + `border:1px solid ${on ? 'var(--ax-accent)' : 'var(--ax-line)'};background:${on ? 'var(--ax-sel)' : 'var(--ax-bg3)'};color:var(--ax-text);`;
  }

  // ─── form helpers (drafts live on the module so re-renders keep typed text) ──
  private static readonly INPUT_CSS = 'width:100%;background:var(--ax-bg2);color:var(--ax-text);border:1px solid var(--ax-line);border-radius:var(--ax-radius);padding:4px 6px;box-sizing:border-box;font:12px var(--ax-font)';
  private field(label: string, value: string, onInput: (v: string) => void, placeholder = ''): HTMLDivElement {
    const row = document.createElement('div'); row.style.cssText = 'margin:4px 0';
    const l = document.createElement('div'); l.textContent = label; l.style.cssText = 'color:var(--ax-soft);font-size:11px;margin-bottom:2px';
    const inp = document.createElement('input'); inp.value = value; inp.placeholder = placeholder; inp.style.cssText = AtlasEditModeModule.INPUT_CSS;
    inp.oninput = () => onInput(inp.value);
    row.append(l, inp);
    return row;
  }
  private area(label: string, value: string, onInput: (v: string) => void, rows = 4, placeholder = '', mono = false): HTMLDivElement {
    const row = document.createElement('div'); row.style.cssText = 'margin:4px 0';
    const l = document.createElement('div'); l.textContent = label; l.style.cssText = 'color:var(--ax-soft);font-size:11px;margin-bottom:2px';
    const ta = document.createElement('textarea'); ta.value = value; ta.rows = rows; ta.placeholder = placeholder; ta.spellcheck = false;
    ta.style.cssText = AtlasEditModeModule.INPUT_CSS + `;resize:vertical;line-height:1.45${mono ? ';font:12px/1.45 var(--ax-mono)' : ''}`;
    ta.oninput = () => onInput(ta.value);
    row.append(l, ta);
    return row;
  }
  private selectBox(label: string, options: Array<{ v: string; l: string }>, value: string, onChange: (v: string) => void): HTMLDivElement {
    const row = document.createElement('div'); row.style.cssText = 'margin:4px 0';
    const l = document.createElement('div'); l.textContent = label; l.style.cssText = 'color:var(--ax-soft);font-size:11px;margin-bottom:2px';
    const sel = document.createElement('select'); sel.style.cssText = AtlasEditModeModule.INPUT_CSS;
    for (const o of options) { const op = document.createElement('option'); op.value = o.v; op.textContent = o.l; if (o.v === value) op.selected = true; sel.appendChild(op); }
    sel.onchange = () => onChange(sel.value);
    row.append(l, sel);
    return row;
  }
  private check(label: string, value: boolean, onChange: (v: boolean) => void, title = ''): HTMLLabelElement {
    const lab = document.createElement('label'); lab.style.cssText = 'display:flex;gap:6px;align-items:center;margin:4px 0;color:var(--ax-text);cursor:pointer'; lab.title = title;
    const cb = document.createElement('input'); cb.type = 'checkbox'; cb.checked = value; cb.onchange = () => onChange(cb.checked);
    const t = document.createElement('span'); t.textContent = label;
    lab.append(cb, t);
    return lab;
  }
  private note(text: string): HTMLDivElement { const d = document.createElement('div'); d.style.cssText = 'color:var(--ax-muted);margin:4px 0'; d.textContent = text; return d; }

  private setTool(t: Tool): void {
    this.tool = t;
    for (const [id, b] of this.toolBtns) b.style.cssText = this.btnCss(id === t);
    // A ghost belongs to ONE tool: shapes/prefabs/uploads → Place, people → NPC / Versona.
    const ghostTool = this.placeChoice ? (this.placeChoice.kind === 'npc' ? 'npc' : this.placeChoice.kind === 'versona' ? 'versona' : this.placeChoice.kind === 'zone' ? 'audio' : this.placeChoice.kind === 'device' ? 'devices' : 'place') : null;
    if (ghostTool !== t) this.clearGhost();
    if (t !== 'sculpt' && t !== 'paint') this.hideBrush();
    if (this.gizmo) { if ((t === 'move' || t === 'rotate' || t === 'scale') && this.sel) this.attachGizmo(); else this.gizmo.detach(); }
    const def = TOOLS.find((x) => x.id === t)!;
    this.setStatus(`${def.label} — ${def.hint}   ·   fly: WASD · Space/C up/down · Shift boost · hold RMB to look`);
    this.renderPanel();
  }

  private setStatus(text: string): void { if (this.status) this.status.textContent = text; }
  /** Editor feedback goes BOTH to the HUD status line and the editor's own status bar (the HUD line hides behind Edit Mode chrome — refusals were invisible). */
  private flash(text: string): void { bus.emit('atlas:status', { text }); this.setStatus(text); }

  // ─── context panel ───────────────────────────────────────────────────────

  private h(title: string): HTMLDivElement {
    const d = document.createElement('div');
    d.textContent = title;
    d.style.cssText = 'font:700 11px var(--ax-mono);color:var(--ax-muted);margin:8px 0 6px;letter-spacing:.04em';
    return d;
  }
  private btn(label: string, fn: () => void, danger = false): HTMLButtonElement {
    const b = document.createElement('button');
    b.textContent = label;
    b.style.cssText = this.btnCss(false) + (danger ? 'color:var(--ax-bad);border-color:var(--ax-line);' : '') + 'margin:2px 4px 2px 0';
    b.onclick = fn;
    return b;
  }
  private slider(label: string, min: number, max: number, step: number, value: number, onChange: (v: number) => void): HTMLDivElement {
    const row = document.createElement('div');
    row.style.cssText = 'display:flex;gap:8px;align-items:center;margin:4px 0';
    const l = document.createElement('span'); l.textContent = label; l.style.cssText = 'min-width:76px;color:var(--ax-soft)';
    const inp = document.createElement('input'); inp.type = 'range'; inp.min = String(min); inp.max = String(max); inp.step = String(step); inp.value = String(value); inp.style.flex = '1';
    const v = document.createElement('span'); v.textContent = String(value); v.style.cssText = 'min-width:34px;text-align:right;color:var(--ax-text)';
    inp.oninput = () => { const n = Number(inp.value); v.textContent = String(n); onChange(n); };
    row.append(l, inp, v);
    return row;
  }

  private renderPanel(): void {
    const p = this.panel;
    if (!p) return;
    p.innerHTML = '';
    const conn = atlas.conn;
    const wid = atlas.currentWorldId;
    if (!conn || wid === null) return;
    const world = conn.db.world.worldId.find(wid);
    const head = document.createElement('div');
    const hb = document.createElement('b'); hb.style.color = 'var(--ax-good)';
    hb.textContent = this.isCommons() ? 'The Commons' : (world?.name ?? 'World'); // user-named worlds: textContent, never markup
    const hs = document.createElement('span'); hs.style.color = 'var(--ax-muted)';
    hs.textContent = ` · ${this.isCommons() ? 'server admin' : this.caps.admin ? 'owner/admin' : 'build grant'} · undo ${this.stack.depth}`;
    head.append(hb, hs);
    p.appendChild(head);

    const selInfo = (): void => this.renderSelection(p, conn, wid);

    switch (this.tool) {
      case 'select': case 'move': case 'rotate': case 'scale': {
        selInfo();
        p.appendChild(this.h('SNAP'));
        p.appendChild(this.btn(this.snap ? 'Snap: ON (X)' : 'Snap: off (X)', () => { this.snap = !this.snap; this.applySnap(); this.renderPanel(); }));
        this.renderOutliner(p);
        break;
      }
      case 'place': {
        p.appendChild(this.h(this.isCommons() ? 'COMMONS CATALOG (prefabs)' : 'SHAPES'));
        if (this.isFreeform()) {
          const row = document.createElement('div');
          for (const prim of REZ_PRIM_TYPES) row.appendChild(this.btn(prim, () => this.setPlaceChoice({ kind: 'prim', key: prim, scale: 1 })));
          p.appendChild(row);
          p.appendChild(this.h('MY UPLOADS (ready)'));
          const ups = [...conn.db.assetUpload.iter()].filter((u) => u.uploader.toHexString() === atlas.identityHex && u.status === 'ready');
          if (ups.length === 0) { const d = document.createElement('div'); d.style.color = 'var(--ax-muted)'; d.textContent = 'None yet — the Upload tool (0) takes a .glb.'; p.appendChild(d); }
          for (const u of ups.slice(0, 20)) p.appendChild(this.btn(u.originalFilename, () => this.setPlaceChoice({ kind: 'upload', key: u.originalFilename, assetId: u.assetId, scale: 1 })));
          p.appendChild(this.h('MY PREFABS (this device)'));
          const prefabs = loadPrefabs();
          if (prefabs.length === 0) p.appendChild(this.note('None yet — select some parts and press "Save as prefab" in the inspector.'));
          for (const def of prefabs.slice().reverse()) {
            const r = document.createElement('div'); r.style.cssText = 'display:flex;gap:4px;align-items:center;margin:2px 0';
            const b = this.btn(`${def.name} (${def.parts.length})`, () => this.setPlaceChoice({ kind: 'prefabset', key: def.name, scale: 1, prefab: def })); b.style.flex = '1';
            const x = this.btn('✕', () => { if (window.confirm(`Delete prefab "${def.name}" from this device?`)) { savePrefabs(loadPrefabs().filter((q) => q.name !== def.name)); this.renderPanel(); } }, true);
            r.append(b, x); p.appendChild(r);
          }
        }
        p.appendChild(this.h('CATALOG'));
        const search = document.createElement('input'); search.placeholder = 'filter… (e.g. tower, civa, lamp)'; search.style.cssText = 'width:100%;background:var(--ax-bg2);color:var(--ax-text);border:1px solid var(--ax-line);border-radius:var(--ax-radius);padding:4px 6px;margin-bottom:6px';
        const list = document.createElement('div'); list.style.cssText = 'max-height:320px;overflow:auto';
        const render = (): void => {
          list.innerHTML = '';
          const q = search.value.toLowerCase();
          // Group: the two civilizations' CITY BUILDINGS first (the settlement
          // packs — placeable in any world you may edit), then structures,
          // props and decor.
          const groups: Array<[string, (k: string) => boolean]> = [
            [`City buildings — ${CIV_TITLE.a}`, (k) => k.startsWith('structure:civa_')],
            [`City buildings — ${CIV_TITLE.b}`, (k) => k.startsWith('structure:civb_')],
            ['Structures', (k) => k.startsWith('structure:') && !/^structure:civ[ab]_/.test(k)],
            ['Props', (k) => k.startsWith('prop:')],
            ['Decor', (k) => k.startsWith('decor:')],
          ];
          const keys = [...conn.db.prefabModel.iter()].filter((pm) => pm.assetId !== 0n).map((pm) => pm.prefabKey).sort();
          for (const [title, match] of groups) {
            const ks = keys.filter((k) => match(k) && (!q || k.toLowerCase().includes(q)));
            if (ks.length === 0) continue;
            const hd = document.createElement('div'); hd.textContent = `${title} (${ks.length})`;
            hd.style.cssText = 'font:700 10px var(--ax-mono);color:var(--ax-soft);margin:6px 0 3px';
            list.appendChild(hd);
            for (const k of ks.slice(0, 80)) {
              list.appendChild(this.btn(k.replace(/^[a-z]+:(civ[ab]_)?/, '').replace(/_/g, ' '), () => this.setPlaceChoice({ kind: 'prefab', key: k, scale: 1 })));
            }
            if (ks.length > 80) { const more = document.createElement('div'); more.style.color = 'var(--ax-muted)'; more.textContent = `… ${ks.length - 80} more — filter to narrow`; list.appendChild(more); }
          }
        };
        search.oninput = render; render();
        p.append(search, list);
        if (this.isCommons()) {
          p.appendChild(this.h('DEEDS'));
          for (const d of [...conn.db.structureDeed.iter()].filter((d) => d.owner.toHexString() === atlas.identityHex && d.packed).slice(0, 12)) {
            p.appendChild(this.btn(`Place ${d.structureType.replace(/_/g, ' ')}`, () => { this.exit(); bus.emit('atlas:structure-place', { deedId: d.deedId.toString(), structureType: d.structureType }); }));
          }
        }
        break;
      }
      case 'sculpt': {
        p.appendChild(this.h('TERRAIN BRUSH'));
        p.appendChild(this.slider('Radius', 2, 80, 1, this.brushR, (v) => { this.brushR = v; }));
        p.appendChild(this.slider('Strength', 0.25, 12, 0.25, this.brushStrength, (v) => { this.brushStrength = v; }));
        const d = document.createElement('div'); d.style.color = 'var(--ax-muted)'; d.innerHTML = '<b>LMB</b> raise · <b>Ctrl+LMB</b> lower · <b>Alt+LMB</b> flatten to the brush height'; p.appendChild(d);
        p.appendChild(this.h('STROKES'));
        p.append(
          this.btn('Undo last stroke', () => void conn.reducers.terrainEditUndo({ worldId: wid }).catch((e: Error) => this.flash(e.message))),
          this.btn('Reset terrain…', () => { if (window.confirm('Remove EVERY sculpt and paint stroke in this world?')) void conn.reducers.terrainEditClear({ worldId: wid }).catch((e: Error) => this.flash(e.message)); }, true),
        );
        const n = document.createElement('div'); n.style.color = 'var(--ax-muted)';
        let count = 0; for (const e of conn.db.terrainEdit.iter()) if (e.worldId === wid) count++;
        n.textContent = `${count} strokes in this world (budget 6000)`; p.appendChild(n);
        break;
      }
      case 'paint': {
        p.appendChild(this.h('GROUND LAYER'));
        const layers = this.isCommons() ? COMMONS_LAYERS : PERSONAL_LAYERS;
        const row = document.createElement('div');
        layers.forEach((name, i) => { const b = this.btn(name, () => { this.paintLayer = i; this.renderPanel(); }); if (i === this.paintLayer) b.style.cssText = this.btnCss(true) + 'margin:2px 4px 2px 0'; row.appendChild(b); });
        p.appendChild(row);
        if (this.isCommons()) { const d = document.createElement('div'); d.style.color = 'var(--ax-muted)'; d.textContent = `(${BIOMES.length} biome grounds — the same textures the world already streams)`; p.appendChild(d); }
        p.appendChild(this.slider('Radius', 2, 80, 1, this.brushR, (v) => { this.brushR = v; }));
        p.appendChild(this.slider('Strength', 0.1, 1, 0.05, this.paintStrength, (v) => { this.paintStrength = v; }));
        p.append(this.btn('Undo last stroke', () => void conn.reducers.terrainEditUndo({ worldId: wid }).catch((e: Error) => this.flash(e.message))));
        break;
      }
      case 'interior': {
        selInfo();
        if (this.sel && this.sel.kind !== 'part' && this.sel.kind !== 'placement') {
          const d = document.createElement('div'); d.style.color = 'var(--ax-muted)';
          d.textContent = 'Interiors are carved from freeform parts and admin placements; a deed structure already has its own door.';
          p.appendChild(d);
        } else if (this.sel) {
          const s = this.sel;
          const markId = `mark:${s.kind}:${s.id}`;
          const on = enterables.has(markId) || [...conn.db.interiorMarker.iter()].some((m) => m.worldId === wid && m.refKind === s.kind && m.refId.toString() === s.id);
          p.appendChild(this.h('INTERIOR'));
          p.appendChild(this.btn(on ? 'Enterable: ON — make solid' : 'Make this building ENTERABLE', () =>
            void conn.reducers.setInteriorMarker({ worldId: wid, refKind: s.kind, refId: BigInt(s.id), enabled: !on })
              .then(() => { this.flash(on ? 'Interior removed' : 'Interior carved — walk to its door and press E'); setTimeout(() => this.renderPanel(), 300); })
              .catch((e: Error) => this.flash(e.message))));
          const d = document.createElement('div'); d.style.color = 'var(--ax-muted)';
          d.textContent = 'An enterable building gets a door, floors, stairs and walls carved from its own size. Furnish it with the Place tool while standing inside; on the Commons, owned deed structures also take crafted furniture (F6 → Place here).';
          p.appendChild(d);
        }
        break;
      }
      case 'script': {
        selInfo();
        if (this.sel && this.sel.kind === 'part') {
          const id = this.sel.id;
          const name = `obj_${id}`;
          const existing = [...conn.db.worldScript.iter()].find((w) => w.worldId === wid && w.name === name);
          p.appendChild(this.h(`SCRIPT · ${name}`));
          const ta = document.createElement('textarea');
          ta.spellcheck = false;
          ta.style.cssText = 'width:100%;height:220px;background:var(--ax-bg2);color:var(--ax-text);border:1px solid var(--ax-line);border-radius:var(--ax-radius);font:12px/1.45 var(--ax-mono);padding:6px;resize:vertical;box-sizing:border-box';
          ta.value = existing?.source ?? [
            `-- Object script for part #${id}`,
            `local ME = "${id}"`,
            'return {',
            '  on_init = function() world.log("object ' + id + ' ready") end,',
            '  on_interact = function(part_id, dist)',
            '    if part_id ~= ME then return end',
            '    world.toast("You pressed E on me!")',
            '    world.particles(world.part_x(ME), world.part_y(ME) + 1, world.part_z(ME), "spark")',
            '  end,',
            '}',
          ].join('\n');
          p.appendChild(ta);
          const row = document.createElement('div');
          row.append(
            this.btn('Save & run', () => void conn.reducers.setWorldScript({ worldId: wid, name, source: ta.value, enabled: true })
              .then(() => this.flash('Script saved — press E on the object in Explore mode')).catch((e: Error) => this.flash(e.message))),
            this.btn('Open in Lua IDE (I)', () => bus.emit('atlas:ide-open', { kind: 'world', name, source: ta.value })),
          );
          p.appendChild(row);
          const d = document.createElement('div'); d.style.color = 'var(--ax-muted)';
          d.textContent = 'on_interact fires when a player presses E within 3.5 m of a part. The world.* bridge gives toasts, particles, tones, tweens, props and more — see the IDE reference.';
          p.appendChild(d);
        } else if (this.sel) {
          const d = document.createElement('div'); d.style.color = 'var(--ax-muted)'; d.textContent = 'Scripts attach to build parts (Commons prefab placements are static props).'; p.appendChild(d);
        }
        break;
      }
      case 'npc': {
        this.renderNpcPanel(p, conn, wid);
        break;
      }
      case 'env': {
        this.renderEnvPanel(p, conn, wid);
        break;
      }
      case 'audio': {
        this.renderAudioPanel(p, conn, wid);
        break;
      }
      case 'devices': {
        this.renderDevicesPanel(p, conn, wid);
        break;
      }
      case 'versona': {
        this.renderVersonaPanel(p, conn, wid);
        break;
      }
      case 'upload': {
        p.appendChild(this.h('UPLOAD A .GLB'));
        const cls = document.createElement('select');
        for (const c of ['mesh', 'decor', 'prop', 'structure', 'foliage']) { const o = document.createElement('option'); o.textContent = c; cls.appendChild(o); }
        cls.style.cssText = 'background:var(--ax-bg2);color:var(--ax-text);border:1px solid var(--ax-line);border-radius:var(--ax-radius);padding:3px 6px;margin-right:6px';
        const file = document.createElement('input'); file.type = 'file'; file.accept = '.glb';
        file.onchange = async () => {
          const f = file.files?.[0];
          if (!f) return;
          const rawKey = `${atlas.identityHex ?? 'anon'}-${f.name}`.replace(/\s+/g, '_');
          const uploadBase = ASSET_STORE_BASE.replace(/\/[^/]+$/, '/atlas-uploads');
          try { await fetch(`${uploadBase}/${rawKey}`, { method: 'PUT', body: await f.arrayBuffer(), headers: { 'Content-Type': 'model/gltf-binary' } }); } catch { /* status via asset_upload */ }
          conn.reducers.beginUpload({ originalFilename: f.name, rawUri: rawKey, useClass: cls.value, worldId: wid })
            .then(() => this.flash('Uploaded — validating; it appears under Place → My uploads when ready'))
            .catch((e: Error) => this.flash(`Upload refused: ${e.message}`));
          file.value = '';
          setTimeout(() => this.renderPanel(), 800);
        };
        const row = document.createElement('div'); row.append(cls, file); p.appendChild(row);
        p.appendChild(this.h('MY UPLOADS'));
        const mine = [...conn.db.assetUpload.iter()].filter((u) => u.uploader.toHexString() === atlas.identityHex).sort((a, b) => Number(b.uploadId - a.uploadId)).slice(0, 10);
        if (mine.length === 0) { const d = document.createElement('div'); d.style.color = 'var(--ax-muted)'; d.textContent = 'No uploads yet.'; p.appendChild(d); }
        for (const u of mine) {
          const r = document.createElement('div'); r.style.cssText = 'display:flex;gap:6px;align-items:center;margin:3px 0';
          const nm = document.createElement('span'); nm.textContent = u.originalFilename; nm.style.flex = '1';
          const st = document.createElement('span'); st.textContent = u.status; st.style.color = u.status === 'ready' ? 'var(--ax-good)' : u.status === 'rejected' ? 'var(--ax-bad)' : 'var(--ax-hi)';
          r.append(nm, st);
          if (u.status === 'ready' && this.isFreeform()) r.appendChild(this.btn('Place', () => { this.setTool('place'); this.setPlaceChoice({ kind: 'upload', key: u.originalFilename, assetId: u.assetId, scale: 1 }); }));
          p.appendChild(r);
        }
        const d = document.createElement('div'); d.style.color = 'var(--ax-muted)'; d.textContent = 'Your own uploads place in your worlds immediately; others may place them only after moderation.'; p.appendChild(d);
        break;
      }
    }
  }

  // ─── selection inspector + outliner ──────────────────────────────────────

  private inspectorInputs: Array<{ el: HTMLInputElement; get: () => number }> = [];
  /** Live numbers during a gizmo drag (never fights a field the user is typing in). */
  private refreshInspector(): void {
    for (const f of this.inspectorInputs) if (document.activeElement !== f.el) f.el.value = f.get().toFixed(2);
  }

  private numField(label: string, get: () => number, set: (v: number) => void): HTMLDivElement {
    const row = document.createElement('div'); row.style.cssText = 'display:flex;gap:6px;align-items:center;margin:3px 0';
    const l = document.createElement('span'); l.textContent = label; l.style.cssText = 'min-width:44px;color:var(--ax-soft)';
    const inp = document.createElement('input'); inp.type = 'number'; inp.step = '0.1'; inp.value = get().toFixed(2);
    inp.style.cssText = AtlasEditModeModule.INPUT_CSS + ';width:auto;flex:1';
    inp.onkeydown = (e) => { e.stopPropagation(); if (e.key === 'Enter') inp.blur(); };
    inp.onchange = () => { const v = Number(inp.value); if (Number.isFinite(v)) set(v); };
    row.append(l, inp);
    this.inspectorInputs.push({ el: inp, get });
    return row;
  }

  /** Apply one inspector field to the primary selection as a command. */
  private async applyInspector(mutate: (mesh: THREE.Object3D) => void): Promise<void> {
    const s = this.sel;
    if (!s) return;
    const before = xfOf(s.mesh);
    const snap = s.kind === 'part' ? this.partSnapshot(s.id) : null;
    mutate(s.mesh);
    const cmd = this.memberCmd(s, before, snap, s.kind === 'npc' ? this.npcRow(s.id) : null, s.kind === 'zone' ? this.zoneRow(s.id) : null, s.kind === 'device' ? this.devices.rowOf(s.id) : null);
    if (!cmd) return;
    try { await this.stack.run(cmd); } catch (e) { this.flash(`Edit refused: ${(e as Error).message}`); }
    this.renderPanel();
  }

  private renderSelection(p: HTMLDivElement, conn: NonNullable<typeof atlas.conn>, wid: bigint): void {
    this.inspectorInputs = [];
    void wid;
    if (this.sels.length === 0) { p.appendChild(this.note('Nothing selected — click an object; drag a box around several; Shift-click adds or removes one.')); return; }
    const kindLabel = (k: SelKind): string => k === 'part' ? 'PART' : k === 'placement' ? 'PLACEMENT' : k === 'npc' ? 'NPC' : k === 'versona' ? 'VERSONA' : k === 'zone' ? 'AUDIO ZONE' : `DEVICE · ${(this.devices.rowOf(this.sel?.id ?? '')?.kind ?? '').toUpperCase()}`;
    if (this.sels.length > 1) {
      const counts = new Map<SelKind, number>();
      for (const m of this.sels) counts.set(m.kind, (counts.get(m.kind) ?? 0) + 1);
      p.appendChild(this.h(`${this.sels.length} SELECTED · ${[...counts].map(([k, n]) => `${n} ${kindLabel(k).toLowerCase()}${n > 1 ? 's' : ''}`).join(' · ')}`));
      const row = document.createElement('div');
      row.append(this.btn('Move (2)', () => this.setTool('move')), this.btn('Rotate (3)', () => this.setTool('rotate')), this.btn('Scale (4)', () => this.setTool('scale')),
        this.btn('Drop to floor', () => void this.dropToFloor()), this.btn('Duplicate', () => void this.duplicateSelected()), this.btn('Delete', () => void this.deleteSelected(), true));
      if (this.isFreeform() && (counts.get('part') ?? 0) > 0) row.append(this.btn('Save as prefab', () => this.savePrefabFromSelection()));
      p.appendChild(row);
      p.appendChild(this.note('Grouped transforms pivot on the selection centroid and land as ONE undo step. Shift-click an object to drop it from the group.'));
      return;
    }
    const s = this.sel!;
    p.appendChild(this.h(`${kindLabel(s.kind)} #${s.id}`));
    const m = s.mesh;
    p.appendChild(this.numField('X', () => m.position.x, (v) => void this.applyInspector((o) => { o.position.x = v; })));
    p.appendChild(this.numField('Y', () => m.position.y, (v) => void this.applyInspector((o) => { o.position.y = v; })));
    p.appendChild(this.numField('Z', () => m.position.z, (v) => void this.applyInspector((o) => { o.position.z = v; })));
    if (s.kind !== 'versona' && s.kind !== 'zone') {
      p.appendChild(this.numField('Yaw °', () => THREE.MathUtils.radToDeg(new THREE.Euler().setFromQuaternion(m.quaternion, 'YXZ').y),
        (v) => void this.applyInspector((o) => { const e = new THREE.Euler().setFromQuaternion(o.quaternion, 'YXZ'); e.y = THREE.MathUtils.degToRad(v); o.quaternion.setFromEuler(e); })));
    }
    if (s.kind !== 'versona') {
      p.appendChild(this.numField(s.kind === 'zone' ? 'Radius' : 'Scale', () => m.scale.x, (v) => void this.applyInspector((o) => { o.scale.setScalar(Math.max(0.01, v)); })));
    }
    if (s.kind === 'part') {
      const snap = this.partSnapshot(s.id);
      if (snap) {
        p.appendChild(this.check('Physics (solid collider)', snap.physics, (on) => {
          void this.stack.run(new EditPartCmd(on ? 'Make solid' : 'Make passable', snap, { ...snap, physics: on })).catch((e: Error) => this.flash(e.message)).then(() => this.renderPanel());
        }, 'Visitors collide with solid parts'));
      }
    }
    const row = document.createElement('div');
    if (s.kind === 'versona') row.append(this.btn('Move (2)', () => this.setTool('move')), this.btn('Profile (=)', () => this.setTool('versona')), this.btn('Delete', () => void this.deleteSelected(), true));
    else if (s.kind === 'zone') row.append(this.btn('Move (2)', () => this.setTool('move')), this.btn('Resize (4)', () => this.setTool('scale')), this.btn('Zone settings (])', () => this.setTool('audio')), this.btn('Delete', () => void this.deleteSelected(), true));
    else if (s.kind === 'device') row.append(this.btn('Move (2)', () => this.setTool('move')), this.btn('Rotate (3)', () => this.setTool('rotate')), this.btn('Resize (4)', () => this.setTool('scale')), this.btn('Settings (;)', () => this.setTool('devices')), this.btn('Duplicate', () => void this.duplicateSelected()), this.btn('Delete', () => void this.deleteSelected(), true));
    else row.append(
      this.btn('Move (2)', () => this.setTool('move')), this.btn('Rotate (3)', () => this.setTool('rotate')), this.btn('Scale (4)', () => this.setTool('scale')),
      this.btn('Drop to floor', () => void this.dropToFloor()), this.btn('Duplicate', () => void this.duplicateSelected()), this.btn('Delete', () => void this.deleteSelected(), true),
    );
    if (s.kind === 'npc') row.append(this.btn('Script (-)', () => this.setTool('npc')));
    if (s.kind === 'part' && this.isFreeform()) row.append(this.btn('Save as prefab', () => this.savePrefabFromSelection()));
    p.appendChild(row);
  }

  /** Outliner rows: every selectable with its display label (user names → textContent only). */
  private outlinerEntries(): Array<{ s: Selection; label: string }> {
    const conn = atlas.conn;
    const labelOf = (s: Selection): string => {
      if (s.kind === 'part') { const snap = this.partSnapshot(s.id); return `▣ #${s.id} ${snap ? (snap.primType === 'mesh' ? snap.prefabKey.replace(/^[a-z]+:/, '') : snap.primType) : ''}`; }
      if (s.kind === 'placement') { const r = this.admin.placementRow(s.id); return `▣ #${s.id} ${r?.prefabKey.replace(/^[a-z]+:/, '') ?? ''}`; }
      if (s.kind === 'npc') return `☺ ${this.npcs.get(s.id)?.name() ?? s.id}`;
      if (s.kind === 'versona') { const pr = conn?.db.versonaProfile.versonaId.find(BigInt(s.id)); return `✦ ${pr?.name || `${pr?.archetype ?? 'versona'} #${s.id}`}`; }
      if (s.kind === 'device') { const r = this.devices.rowOf(s.id); return `⚙ ${r?.name ?? s.id} (${r?.kind ?? 'device'})`; }
      const z = this.zoneRow(s.id); return `♪ ${z?.name ?? s.id} (${z?.kind ?? 'zone'})`;
    };
    return this.selectables().map((s) => ({ s, label: labelOf(s) }));
  }

  /** Probe: place a catalog choice at (x, z) as if the ghost were clicked there. */
  private async placeAtProbe(kind: 'prim' | 'prefab' | 'npc' | 'versona' | 'zone' | 'device' | 'prefabset', key: string, x: number, z: number): Promise<boolean> {
    const conn = atlas.conn;
    if (!conn || !this.active) return false;
    if (kind === 'prefabset') { const def = loadPrefabs().find((p) => p.name === key); if (!def) return false; this.setPlaceChoice({ kind, key, scale: 1, prefab: def }); }
    else this.setPlaceChoice({ kind, key, scale: 1 });
    if (!this.ghost) return false;
    this.ghost.position.set(x, groundYIn(conn, atlas.currentWorldId, x, z), z);
    this.ghost.visible = true;
    await this.placeAtGhost();
    return true;
  }

  /** Probe: save the selected parts as a prefab without the name prompt. */
  private savePrefabNamed(name: string): boolean {
    const snaps = this.sels.filter((m) => m.kind === 'part').map((m) => this.partSnapshot(m.id)).filter((x): x is PartRowSnapshot => x !== null);
    if (snaps.length === 0) return false;
    savePrefabs(upsertPrefab(loadPrefabs(), capturePrefab(name, snaps)));
    return true;
  }

  /** Probe: move the whole selection by a delta through the same commit path a gizmo drag takes. */
  private async nudgeProbe(dx: number, dy: number, dz: number): Promise<number> {
    if (this.sels.length === 0) return 0;
    const before: MemberBefore[] = this.sels.map((m) => ({
      s: m, pos: m.mesh.position.clone(), quat: m.mesh.quaternion.clone(), scale: m.mesh.scale.clone(), xf: xfOf(m.mesh),
      snap: m.kind === 'part' ? this.partSnapshot(m.id) : null, npc: m.kind === 'npc' ? this.npcRow(m.id) : null, zone: m.kind === 'zone' ? this.zoneRow(m.id) : null, dev: m.kind === 'device' ? this.devices.rowOf(m.id) : null,
    }));
    for (const b of before) b.s.mesh.position.add(new THREE.Vector3(dx, dy, dz));
    const cmds = before.map((b) => this.memberCmd(b.s, b.xf, b.snap, b.npc, b.zone, b.dev)).filter((c): c is BuildCommand => c !== null);
    if (cmds.length === 0) return 0;
    await this.stack.run(cmds.length === 1 ? cmds[0] : new GroupCmd(`Move ${cmds.length} objects`, cmds));
    return cmds.length;
  }

  /** Every editable thing in the world — click selects, Shift-click adds. */
  private renderOutliner(p: HTMLDivElement): void {
    const all = this.selectables();
    p.appendChild(this.h(`OUTLINER (${all.length})`));
    const filter = document.createElement('input'); filter.placeholder = 'filter… (lamp, npc, zone, #12)'; filter.value = this.outlinerFilter;
    filter.style.cssText = AtlasEditModeModule.INPUT_CSS + ';margin-bottom:4px';
    filter.onkeydown = (e) => e.stopPropagation();
    const list = document.createElement('div'); list.style.cssText = 'max-height:260px;overflow:auto;font:11px/1.5 var(--ax-font)';
    const entries = this.outlinerEntries();
    const render = (): void => {
      list.textContent = '';
      const q = this.outlinerFilter.toLowerCase();
      let shown = 0;
      for (const { s, label } of entries) {
        if (q && !label.toLowerCase().includes(q) && !s.kind.includes(q)) continue;
        if (++shown > 200) { const more = document.createElement('div'); more.style.color = 'var(--ax-muted)'; more.textContent = '… filter to narrow'; list.appendChild(more); break; }
        const d = document.createElement('div');
        const on = this.sels.some((m) => m.kind === s.kind && m.id === s.id);
        d.textContent = label; // user text (names) → textContent
        d.style.cssText = `padding:2px 6px;border-radius:var(--ax-radius);cursor:pointer;color:${on ? 'var(--ax-good)' : 'var(--ax-text)'};background:${on ? 'var(--ax-bg3)' : 'transparent'}`;
        d.onmouseenter = () => { if (!on) d.style.background = 'var(--ax-bg3)'; };
        d.onmouseleave = () => { if (!on) d.style.background = 'transparent'; };
        d.onclick = (e) => this.select(s, e.shiftKey);
        list.appendChild(d);
      }
      if (shown === 0) { const none = document.createElement('div'); none.style.color = 'var(--ax-muted)'; none.textContent = all.length ? 'No match.' : 'Nothing placed yet.'; list.appendChild(none); }
    };
    filter.oninput = () => { this.outlinerFilter = filter.value; render(); };
    render();
    p.append(filter, list);
  }

  // ─── audio zones: edit markers ───────────────────────────────────────────

  private zoneColor(kind: string): number { return kind === 'music' ? 0xffd166 : kind === 'sfx' ? 0xff8866 : 0x66c2ff; }

  private syncZoneMarkers(): void {
    const conn = atlas.conn;
    const wid = atlas.currentWorldId;
    if (!conn || wid === null || !this.zoneRoot.parent) return;
    if (!this.zoneGeo) this.zoneGeo = new THREE.SphereGeometry(1, 20, 14);
    const seen = new Set<string>();
    const held = new Set(this.sels.filter((m) => m.kind === 'zone').map((m) => m.id));
    try {
      for (const z of conn.db.audioZone.iter()) {
        if (z.worldId !== wid) continue;
        const id = z.zoneId.toString();
        seen.add(id);
        let m = this.zoneMarkers.get(id);
        if (!m) {
          m = new THREE.Mesh(this.zoneGeo, new THREE.MeshBasicMaterial({ color: this.zoneColor(z.kind), wireframe: true, transparent: true, opacity: 0.45, depthWrite: false }));
          m.userData.zoneId = id;
          this.zoneMarkers.set(id, m);
          this.zoneRoot.add(m);
        }
        (m.material as THREE.MeshBasicMaterial).color.set(this.zoneColor(z.kind));
        if (!held.has(id)) { m.position.set(z.cx, z.cy, z.cz); m.scale.setScalar(Math.max(0.5, z.radius)); }
      }
    } catch { /* churn */ }
    for (const [id, m] of this.zoneMarkers) if (!seen.has(id)) { this.zoneRoot.remove(m); (m.material as THREE.Material).dispose(); this.zoneMarkers.delete(id); }
  }

  private clearZoneMarkers(): void {
    for (const m of this.zoneMarkers.values()) { this.zoneRoot.remove(m); (m.material as THREE.Material).dispose(); }
    this.zoneMarkers.clear();
  }

  // ─── Environment panel ───────────────────────────────────────────────────

  private renderEnvPanel(p: HTMLDivElement, conn: NonNullable<typeof atlas.conn>, wid: bigint): void {
    if (!this.caps.admin) { p.appendChild(this.note('The environment is the owner\'s/admin\'s to set — it changes what every visitor sees.')); return; }
    const sky = conn.db.skyState.worldId.find(wid);
    const row = conn.db.worldEnvironment.worldId.find(wid);
    if (!this.envDraft) {
      const base: WorldEnv = row ? { timeMode: row.timeMode as WorldEnv['timeMode'], fixedTimeOfDay: row.fixedTimeOfDay, weatherMode: row.weatherMode as WorldEnv['weatherMode'], fogMul: row.fogMul, fogColorHex: row.fogColorHex, sunMul: row.sunMul, ambientMul: row.ambientMul, sunTintHex: row.sunTintHex, ambienceKey: row.ambienceKey, ambienceGain: row.ambienceGain } : { ...DEFAULT_WORLD_ENV, fixedTimeOfDay: sky?.timeOfDay ?? 0.5 };
      this.envDraft = { ...base, cycleSeconds: sky?.cycleSeconds || 7200, weather: sky?.weather ?? 'clear' };
    }
    const d = this.envDraft;
    const live = worldEnv();
    p.appendChild(this.h(`SKY CLOCK · now ${todToClock(sky?.timeOfDay ?? 0.5)} · ${sky?.weather ?? '—'} · ${row ? 'authored' : 'engine defaults'}`));
    const modeRow = (label: string, opts: Array<[string, string]>, cur: string, set: (v: string) => void): HTMLDivElement => {
      const r = document.createElement('div'); r.style.cssText = 'display:flex;gap:4px;align-items:center;margin:3px 0';
      const l = document.createElement('span'); l.textContent = label; l.style.cssText = 'min-width:64px;color:var(--ax-soft)'; r.appendChild(l);
      for (const [v, txt] of opts) { const b = this.btn(txt, () => { set(v); this.renderPanel(); }); if (v === cur) b.style.cssText = this.btnCss(true) + 'margin:2px 4px 2px 0'; r.appendChild(b); }
      return r;
    };
    p.appendChild(this.h('TIME'));
    p.appendChild(modeRow('Clock', [['cycle', 'Runs'], ['fixed', 'Held']], d.timeMode, (v) => { d.timeMode = v as WorldEnv['timeMode']; }));
    const timeRow = document.createElement('div'); timeRow.style.cssText = 'display:flex;gap:6px;align-items:center;margin:3px 0';
    const tl = document.createElement('span'); tl.textContent = d.timeMode === 'fixed' ? 'Held at' : 'Set to'; tl.style.cssText = 'min-width:64px;color:var(--ax-soft)';
    const tin = document.createElement('input'); tin.value = todToClock(d.fixedTimeOfDay); tin.style.cssText = AtlasEditModeModule.INPUT_CSS + ';width:70px';
    tin.onkeydown = (e) => { e.stopPropagation(); if (e.key === 'Enter') tin.blur(); };
    const tsl = document.createElement('input'); tsl.type = 'range'; tsl.min = '0'; tsl.max = '1439'; tsl.value = String(Math.round(d.fixedTimeOfDay * 1440)); tsl.style.flex = '1';
    tin.onchange = () => { const t = clockToTod(tin.value); if (t !== null) { d.fixedTimeOfDay = t; tsl.value = String(Math.round(t * 1440)); } else tin.value = todToClock(d.fixedTimeOfDay); };
    tsl.oninput = () => { d.fixedTimeOfDay = Number(tsl.value) / 1440; tin.value = todToClock(d.fixedTimeOfDay); };
    timeRow.append(tl, tin, tsl); p.appendChild(timeRow);
    p.appendChild(this.selectBox('Day length', DAY_LENGTHS.map((x) => ({ v: String(x.seconds), l: x.label })), String(DAY_LENGTHS.some((x) => x.seconds === d.cycleSeconds) ? d.cycleSeconds : 7200), (v) => { d.cycleSeconds = Number(v); }));
    p.appendChild(this.h('WEATHER'));
    p.appendChild(modeRow('Weather', [['auto', 'Rotates daily'], ['fixed', 'Fixed']], d.weatherMode, (v) => { d.weatherMode = v as WorldEnv['weatherMode']; }));
    p.appendChild(this.selectBox(d.weatherMode === 'fixed' ? 'Weather' : 'Weather now', WEATHERS.map((w) => ({ v: w, l: w })), d.weather, (v) => { d.weather = v; }));
    p.appendChild(this.h('LOOK'));
    p.appendChild(this.slider('Fog ×', 0, 4, 0.05, d.fogMul, (v) => { d.fogMul = v; }));
    const tint = (label: string, get: () => string, set: (v: string) => void): HTMLDivElement => {
      const r = document.createElement('div'); r.style.cssText = 'display:flex;gap:6px;align-items:center;margin:3px 0';
      const on = document.createElement('input'); on.type = 'checkbox'; on.checked = get() !== '';
      const l = document.createElement('span'); l.textContent = label; l.style.cssText = 'flex:1;color:var(--ax-soft)';
      const c = document.createElement('input'); c.type = 'color'; c.value = get() || '#88aaff'; c.disabled = !on.checked;
      on.onchange = () => { c.disabled = !on.checked; set(on.checked ? c.value : ''); };
      c.oninput = () => { if (on.checked) set(c.value); };
      r.append(on, l, c); return r;
    };
    p.appendChild(tint('Fog tint', () => d.fogColorHex, (v) => { d.fogColorHex = v; }));
    p.appendChild(this.slider('Sun ×', 0.1, 3, 0.05, d.sunMul, (v) => { d.sunMul = v; }));
    p.appendChild(this.slider('Ambient ×', 0.1, 3, 0.05, d.ambientMul, (v) => { d.ambientMul = v; }));
    p.appendChild(tint('Sun tint', () => d.sunTintHex, (v) => { d.sunTintHex = v; }));
    p.appendChild(this.h('AMBIENCE'));
    p.appendChild(this.selectBox('Sound bed', AMBIENCE_KEYS.map((a) => ({ v: a.key, l: a.label })), d.ambienceKey, (v) => { d.ambienceKey = v; }));
    p.appendChild(this.slider('Bed gain', 0, 1, 0.05, d.ambienceGain, (v) => { d.ambienceGain = v; }));
    const row2 = document.createElement('div');
    row2.append(
      this.btn('Apply to world', () => void conn.reducers.setWorldEnvironment({
        worldId: wid, timeMode: d.timeMode, fixedTimeOfDay: d.fixedTimeOfDay, cycleSeconds: d.cycleSeconds, weatherMode: d.weatherMode, weather: d.weather,
        fogMul: d.fogMul, fogColorHex: d.fogColorHex, sunMul: d.sunMul, ambientMul: d.ambientMul, sunTintHex: d.sunTintHex, ambienceKey: d.ambienceKey, ambienceGain: d.ambienceGain,
      }).then(() => { this.flash('Environment applied — everyone in this world sees it'); setTimeout(() => this.renderPanel(), 300); }).catch((e: Error) => this.flash(`Refused: ${e.message}`))),
      this.btn('Reset to defaults', () => void conn.reducers.resetWorldEnvironment({ worldId: wid }).then(() => { this.envDraft = null; this.flash('Environment reset'); setTimeout(() => this.renderPanel(), 300); }).catch((e: Error) => this.flash(e.message)), true),
    );
    p.appendChild(row2);
    p.appendChild(this.note(`A held clock keeps the sun where you put it; fixed weather stops the daily rotation. Fog/sun/ambient multiply the weather and time-of-day look everyone renders by (live now: fog ×${live.fogMul}, sun ×${live.sunMul}). The sound bed replaces the biome ambience in your world.`));
    this.renderRules(p, conn, wid);
  }

  // ─── Audio panel ─────────────────────────────────────────────────────────

  private audioClips(conn: NonNullable<typeof atlas.conn>): Array<{ id: bigint; name: string }> {
    const out: Array<{ id: bigint; name: string }> = [];
    try { for (const a of conn.db.renderAsset.iter()) if (a.kind === 'audio') out.push({ id: a.renderAssetId, name: (a as unknown as { name?: string }).name ?? `audio #${a.renderAssetId}` }); } catch { /* churn */ }
    return out.sort((a, b) => a.name.localeCompare(b.name));
  }

  private zoneForm(p: HTMLDivElement, conn: NonNullable<typeof atlas.conn>, d: { assetId: bigint; kind: string; radius: number; gain: number; loop: boolean; fade: number }): void {
    const clips = this.audioClips(conn);
    if (clips.length === 0) p.appendChild(this.note('No audio clips in the catalog yet (render_asset kind=audio).'));
    p.appendChild(this.selectBox('Clip', [{ v: '0', l: '— pick a clip —' }, ...clips.map((c) => ({ v: c.id.toString(), l: c.name }))], d.assetId.toString(), (v) => { d.assetId = BigInt(v); }));
    p.appendChild(this.selectBox('Kind', AUDIO_KINDS.map((k) => ({ v: k, l: k })), d.kind, (v) => { d.kind = v; }));
    p.appendChild(this.slider('Radius (m)', 1, 200, 1, d.radius, (v) => { d.radius = v; if (this.ghost && this.placeChoice?.kind === 'zone') { const mesh = this.ghost.children[0]; if (mesh) mesh.scale.setScalar(Math.max(0.5, v) / Math.max(0.5, this.zoneDraft.radius || 1)); } }));
    p.appendChild(this.slider('Gain', 0, 1.5, 0.05, d.gain, (v) => { d.gain = v; }));
    p.appendChild(this.check('Loop', d.loop, (v) => { d.loop = v; }, 'Beds loop; one-shots play on entry'));
    p.appendChild(this.slider('Fade (s)', 0, 10, 0.5, d.fade, (v) => { d.fade = v; }));
  }

  private renderAudioPanel(p: HTMLDivElement, conn: NonNullable<typeof atlas.conn>, wid: bigint): void {
    this.syncZoneMarkers();
    const sel = this.sel?.kind === 'zone' ? this.sel : null;
    const z = sel ? this.zoneRow(sel.id) : null;
    if (z) {
      p.appendChild(this.h(`ZONE · ${z.name}`));
      const d = { assetId: z.audioAssetId, kind: z.kind, radius: z.radius, gain: z.gain, loop: z.loop, fade: z.fadeDuration };
      this.zoneForm(p, conn, d);
      const row = document.createElement('div');
      row.append(
        this.btn('Apply', () => void this.stack.run(new ZoneUpsertCmd(`Edit zone ${z.name}`, z, { ...z, audioAssetId: d.assetId, kind: d.kind, radius: d.radius, gain: d.gain, loop: d.loop, fadeDuration: d.fade }))
          .then(() => { this.flash('Zone updated'); setTimeout(() => this.renderPanel(), 250); }).catch((e: Error) => this.flash(`Refused: ${e.message}`))),
        this.btn('Move (2)', () => this.setTool('move')), this.btn('Resize (4)', () => this.setTool('scale')), this.btn('Delete', () => void this.deleteSelected(), true),
      );
      p.appendChild(row);
      p.appendChild(this.note('Visitors hear the clip while inside the sphere (entering starts it, the fade eases it in/out). The Scale gizmo resizes the radius.'));
      return;
    }
    p.appendChild(this.h('NEW ZONE'));
    this.zoneForm(p, conn, this.zoneDraft);
    p.appendChild(this.btn('Place zone (click the ground)', () => { if (this.zoneDraft.assetId === 0n) { this.flash('Pick a clip first'); return; } this.setPlaceChoice({ kind: 'zone', key: 'zone', scale: 1 }); }));
    const zones = [...conn.db.audioZone.iter()].filter((r) => r.worldId === wid).sort((a, b) => (a.zoneId < b.zoneId ? -1 : 1));
    p.appendChild(this.h(`ZONES IN THIS WORLD (${zones.length})`));
    if (zones.length === 0) p.appendChild(this.note('None yet — zones show as wire spheres while you edit (blue ambience · gold music · orange sfx).'));
    for (const r of zones) {
      const line = document.createElement('div'); line.style.cssText = 'display:flex;gap:6px;align-items:center;margin:2px 0';
      const nm = document.createElement('span'); nm.textContent = `${r.name} · ${r.kind} · ${Math.round(r.radius)} m`; nm.style.flex = '1';
      line.append(nm, this.btn('Select', () => { const m = this.zoneMarkers.get(r.zoneId.toString()); if (m) this.select({ kind: 'zone', id: r.zoneId.toString(), mesh: m }); }));
      p.appendChild(line);
    }
  }

  // ─── Devices panel ───────────────────────────────────────────────────────

  /** A generic form over a kind's FieldSpec list (the device's config draft). */
  private fieldForm(p: HTMLDivElement, fields: FieldSpec[], cfg: Record<string, unknown>, conn: NonNullable<typeof atlas.conn>): void {
    for (const f of fields) {
      const v = cfg[f.key];
      switch (f.type) {
        case 'number': p.appendChild(f.min !== undefined && f.max !== undefined && f.step !== undefined
          ? this.slider(f.label, f.min, f.max, f.step, num(v, f.min), (n) => { cfg[f.key] = n; })
          : this.field(f.label, String(num(v, 0)), (t) => { const n = Number(t); if (Number.isFinite(n)) cfg[f.key] = n; })); break;
        case 'text': case 'signal': p.appendChild(this.field(f.label + (f.hint ? ` — ${f.hint}` : ''), str(v), (t) => { cfg[f.key] = t; }, f.type === 'signal' ? 'name:event' : '')); break;
        case 'textarea': p.appendChild(this.area(f.label, str(v), (t) => { cfg[f.key] = t; }, 3, f.hint ?? '')); break;
        case 'bool': p.appendChild(this.check(f.label, bool(v, false), (b) => { cfg[f.key] = b; }, f.hint ?? '')); break;
        case 'color': {
          const row = document.createElement('div'); row.style.cssText = 'display:flex;gap:6px;align-items:center;margin:3px 0';
          const l = document.createElement('span'); l.textContent = f.label; l.style.cssText = 'flex:1;color:var(--ax-soft)';
          const c = document.createElement('input'); c.type = 'color'; c.value = /^#[0-9a-fA-F]{6}$/.test(str(v)) ? str(v) : '#ffffff'; c.oninput = () => { cfg[f.key] = c.value; };
          row.append(l, c); p.appendChild(row); break;
        }
        case 'select': {
          const opts = f.key === 'assetId' ? [] : (f.options ?? []);
          p.appendChild(this.selectBox(f.label, opts, str(v), (t) => { cfg[f.key] = t; })); break;
        }
      }
      if (f.key === 'assetId') {
        // Speaker clips: a picker over the cooked audio catalog beside the raw id field.
        const clips = this.audioClips(conn);
        p.appendChild(this.selectBox('Pick a clip', [{ v: '0', l: '— clip —' }, ...clips.map((c) => ({ v: c.id.toString(), l: c.name }))], str(v, '0'), (t) => { cfg.assetId = t; this.renderPanel(); }));
      }
    }
  }

  private cameraKeyFromView(): { x: number; y: number; z: number; yaw: number; pitch: number } | null {
    const body = atlas.localMesh;
    const cam = body?.children[0] as THREE.Object3D | undefined;
    if (!body || !cam) return null;
    return { x: Math.round(body.position.x * 100) / 100, y: Math.round(body.position.y * 100) / 100, z: Math.round(body.position.z * 100) / 100, yaw: Math.round(atlas.localYaw * 1000) / 1000, pitch: Math.round(cam.rotation.x * 1000) / 1000 };
  }

  private renderDevicesPanel(p: HTMLDivElement, conn: NonNullable<typeof atlas.conn>, wid: bigint): void {
    const sel = this.sel?.kind === 'device' ? this.sel : null;
    const row = sel ? this.devices.rowOf(sel.id) : null;
    if (row) {
      const def = DEVICE_BY_KIND.get(row.kind);
      const id = row.deviceId.toString();
      let d = this.deviceDrafts.get(id);
      if (!d || d.cfg.__rev !== row.updatedAt.microsSinceUnixEpoch.toString()) {
        d = { name: row.name, enabled: row.enabled, cfg: { ...deviceConfig(row.kind, row.configJson), __rev: row.updatedAt.microsSinceUnixEpoch.toString() }, sx: row.sx, sy: row.sy, sz: row.sz };
        this.deviceDrafts.set(id, d);
      }
      const draft = d;
      p.appendChild(this.h(`${(def?.label ?? row.kind).toUpperCase()} · #${id}`));
      if (def?.hint) p.appendChild(this.note(def.hint));
      p.appendChild(this.field('Name (signals use it: name:event)', draft.name, (v) => { draft.name = v; }));
      p.appendChild(this.check('Enabled', draft.enabled, (v) => { draft.enabled = v; }, 'Disabled devices show only to editors'));
      const sizeRow = document.createElement('div'); sizeRow.style.cssText = 'display:flex;gap:4px';
      for (const k of ['sx', 'sy', 'sz'] as const) { const f = this.field(k === 'sx' ? 'Size X' : k === 'sy' ? 'Size Y' : 'Size Z', String(draft[k]), (v) => { const n = Number(v); if (Number.isFinite(n)) draft[k] = Math.max(0.05, n); }); f.style.flex = '1'; sizeRow.appendChild(f); }
      p.appendChild(sizeRow);
      if (def) { p.appendChild(this.h('SETTINGS')); this.fieldForm(p, def.fields, draft.cfg, conn); }
      // Kind-specific authoring tools.
      if (row.kind === 'camera_path') {
        p.appendChild(this.h('KEYFRAMES'));
        const keys = parseCameraKeys(draft.cfg.keys);
        const list = document.createElement('div'); list.style.cssText = 'font:11px var(--ax-mono);color:var(--ax-soft);max-height:120px;overflow:auto';
        keys.forEach((k, i) => { const l = document.createElement('div'); l.textContent = `${i + 1}. (${k.x}, ${k.y}, ${k.z}) yaw ${k.yaw} pitch ${k.pitch}`; list.appendChild(l); });
        if (keys.length === 0) list.textContent = 'none — fly to a view and add keyframes';
        p.appendChild(list);
        const kr = document.createElement('div');
        kr.append(
          this.btn('+ Keyframe from this view', () => { const k = this.cameraKeyFromView(); if (k) { draft.cfg.keys = [...keys, k]; this.renderPanel(); } }),
          this.btn('Remove last', () => { draft.cfg.keys = keys.slice(0, -1); this.renderPanel(); }),
          this.btn('▶ Preview', () => { if (keys.length < 2) { this.flash('Add at least two keyframes'); return; } this.devices.previewCinematic(keys, num(draft.cfg.duration, 8), draft.name); }),
        );
        p.appendChild(kr);
        p.appendChild(this.note('Fly (WASD + RMB look) to each shot and press "+ Keyframe". The flight passes through every key (Catmull-Rom), eased. In Explore mode E on the marker, a signal, or world.play_cinematic(name) plays it.'));
      }
      if (row.kind === 'path') {
        p.appendChild(this.h('WAYPOINTS'));
        const pts = Array.isArray(draft.cfg.points) ? (draft.cfg.points as Array<{ x: number; z: number }>) : [];
        const list = document.createElement('div'); list.style.cssText = 'font:11px var(--ax-mono);color:var(--ax-soft);max-height:120px;overflow:auto';
        pts.forEach((q, i) => { const l = document.createElement('div'); l.textContent = `${i + 1}. (${num(q.x, 0).toFixed(1)}, ${num(q.z, 0).toFixed(1)})`; list.appendChild(l); });
        if (pts.length === 0) list.textContent = 'none — fly over a spot and add waypoints';
        p.appendChild(list);
        const wr = document.createElement('div');
        wr.append(
          this.btn('+ Waypoint under me', () => { const b = atlas.localMesh; if (b) { draft.cfg.points = [...pts, { x: Math.round(b.position.x * 10) / 10, z: Math.round(b.position.z * 10) / 10 }]; this.renderPanel(); } }),
          this.btn('Remove last', () => { draft.cfg.points = pts.slice(0, -1); this.renderPanel(); }),
        );
        p.appendChild(wr);
        p.appendChild(this.note(`NPCs walk it with npc.follow_path("${draft.name}") or a schedule row "path ${draft.name}". A looping path patrols forever.`));
      }
      if (row.kind === 'teleporter') {
        p.appendChild(this.btn('Set target to my position', () => { const b = atlas.localMesh; if (b) { draft.cfg.tx = Math.round(b.position.x * 10) / 10; draft.cfg.ty = Math.round(groundYIn(conn, wid, b.position.x, b.position.z) * 10) / 10; draft.cfg.tz = Math.round(b.position.z * 10) / 10; draft.cfg.tyaw = Math.round(atlas.localYaw * 100) / 100; this.renderPanel(); } }));
      }
      if (def?.emits?.length) p.appendChild(this.note(`Emits: ${def.emits.map((e) => `${draft.name}:${e}`).join(' · ')}`));
      const save = (): void => {
        const { __rev: _r, ...cfg } = draft.cfg; void _r;
        void conn.reducers.editDevice({ deviceId: row.deviceId, name: draft.name.trim() || row.name, enabled: draft.enabled, posX: row.posX, posY: row.posY, posZ: row.posZ, yaw: row.yaw, sx: draft.sx, sy: draft.sy, sz: draft.sz, configJson: JSON.stringify(cfg) })
          .then(() => { this.deviceDrafts.delete(id); this.flash(`${def?.label ?? row.kind} updated`); setTimeout(() => this.renderPanel(), 250); })
          .catch((e: Error) => this.flash(`Refused: ${e.message}`));
      };
      const actions = document.createElement('div');
      actions.append(this.btn('Apply', save), this.btn('Move (2)', () => this.setTool('move')), this.btn('Rotate (3)', () => this.setTool('rotate')), this.btn('Resize (4)', () => this.setTool('scale')),
        this.btn('Test (trigger)', () => { this.devices.trigger(id); }), this.btn('Duplicate', () => void this.duplicateSelected()), this.btn('Delete', () => void this.deleteSelected(), true));
      p.appendChild(actions);
      return;
    }
    p.appendChild(this.h('DEVICE CATALOG'));
    p.appendChild(this.note('Devices DO things and talk by signals: each emits "name:event" (a lever "lever1:on") and obeys the topic in its Listen field. NPC scripts and world scripts hear the same bus (on_signal / npc.signal / world.signal).'));
    for (const g of DEVICE_GROUPS) {
      const kinds = DEVICE_KINDS.filter((k) => k.group === g.key);
      if (kinds.length === 0) continue;
      const hd = document.createElement('div'); hd.textContent = g.title; hd.style.cssText = 'font:700 10px var(--ax-mono);color:var(--ax-soft);margin:6px 0 3px';
      p.appendChild(hd);
      const row = document.createElement('div');
      for (const k of kinds) { const b = this.btn(k.label, () => this.setPlaceChoice({ kind: 'device', key: k.kind, scale: 1 })); b.title = k.hint; row.appendChild(b); }
      p.appendChild(row);
    }
    const all = this.devices.ids().map((id) => this.devices.rowOf(id)!).filter(Boolean);
    p.appendChild(this.h(`DEVICES IN THIS WORLD (${all.length}/200)`));
    if (all.length === 0) p.appendChild(this.note('None yet. Hold Shift while clicking to place several of the same kind.'));
    for (const r of all.slice(0, 60)) {
      const line = document.createElement('div'); line.style.cssText = 'display:flex;gap:6px;align-items:center;margin:2px 0';
      const nm = document.createElement('span'); nm.textContent = `${r.name} · ${DEVICE_BY_KIND.get(r.kind)?.label ?? r.kind}${r.enabled ? '' : ' (off)'}`; nm.style.flex = '1';
      line.append(nm, this.btn('Select', () => { const m = this.devices.deviceMesh(r.deviceId.toString()); if (m) this.select({ kind: 'device', id: r.deviceId.toString(), mesh: m }); }));
      p.appendChild(line);
    }
  }

  // ─── Rules (in the Environment panel) ────────────────────────────────────

  private renderRules(p: HTMLDivElement, conn: NonNullable<typeof atlas.conn>, wid: bigint): void {
    const rules = conn.db.worldRules.worldId.find(wid);
    if (!this.rulesDraft) this.rulesDraft = { objective: rules?.objective ?? '', roundSeconds: rules?.roundSeconds ?? 0, respawnMode: rules?.respawnMode ?? 'spawn', scoreTopic: rules?.scoreTopic ?? '' };
    const d = this.rulesDraft;
    p.appendChild(this.h('GAMEPLAY RULES (optional)'));
    p.appendChild(this.note(rules?.hasSpawn ? `Visitors spawn at (${rules.spawnX.toFixed(1)}, ${rules.spawnZ.toFixed(1)}) — the Spawn point device.` : 'Visitors spawn at the origin — place a Spawn point device (Devices → Rules) to choose.'));
    p.appendChild(this.field('Objective (HUD line)', d.objective, (v) => { d.objective = v; }, 'Light the three lamps'));
    p.appendChild(this.slider('Round (s, 0 = none)', 0, 1800, 30, d.roundSeconds, (v) => { d.roundSeconds = v; }));
    p.appendChild(this.selectBox('Respawn at', [{ v: 'spawn', l: 'the spawn point' }, { v: 'checkpoint', l: 'the last checkpoint' }], d.respawnMode, (v) => { d.respawnMode = v; }));
    p.appendChild(this.field('Score signal (counts on the HUD)', d.scoreTopic, (v) => { d.scoreTopic = v; }, 'lamp:on'));
    const row = document.createElement('div');
    row.append(
      this.btn('Apply rules', () => void conn.reducers.setWorldRules({ worldId: wid, objective: d.objective, roundSeconds: Math.round(d.roundSeconds), respawnMode: d.respawnMode, scoreTopic: d.scoreTopic })
        .then(() => { this.flash('Rules applied'); setTimeout(() => this.renderPanel(), 250); }).catch((e: Error) => this.flash(`Refused: ${e.message}`))),
      this.btn('Clear rules', () => void conn.reducers.resetWorldRules({ worldId: wid }).then(() => { this.rulesDraft = null; this.flash('Rules cleared'); setTimeout(() => this.renderPanel(), 250); }).catch((e: Error) => this.flash(e.message)), true),
    );
    p.appendChild(row);
  }

  // ─── NPC data editors (dialogue tree + schedule) ─────────────────────────

  private renderNpcDataEditors(p: HTMLDivElement, conn: NonNullable<typeof atlas.conn>, row: { npcId: bigint; name: string; modelKey: string; posX: number; posY: number; posZ: number; yaw: number; scale: number; configJson: string }): void {
    const id = row.npcId.toString();
    let cfg = this.npcCfgDrafts.get(id);
    if (!cfg) { try { const o = JSON.parse(row.configJson || '{}') as unknown; cfg = o && typeof o === 'object' ? o as Record<string, unknown> : {}; } catch { cfg = {}; } this.npcCfgDrafts.set(id, cfg); }
    const draft = cfg;
    const save = (): void => {
      void conn.reducers.editNpc({ npcId: row.npcId, name: row.name, modelKey: row.modelKey, configJson: JSON.stringify(draft), posX: row.posX, posY: row.posY, posZ: row.posZ, yaw: row.yaw, scale: row.scale })
        .then(() => { this.npcCfgDrafts.delete(id); this.flash('NPC data saved'); setTimeout(() => this.renderPanel(), 250); }).catch((e: Error) => this.flash(`Refused: ${e.message}`));
    };
    // ── Dialogue tree ──
    p.appendChild(this.h('DIALOGUE TREE (no code)'));
    const tree = parseDialogue(draft.dialogue);
    if (!tree) {
      p.appendChild(this.note('No tree — conversations use the script\'s topics/on_say. A tree replaces that: nodes of NPC text with player choices, conditions on memory, and actions.'));
      p.appendChild(this.btn('New dialogue tree', () => { draft.dialogue = JSON.parse(JSON.stringify(SAMPLE_DIALOGUE)) as DialogueTree; this.renderPanel(); }));
    } else {
      const t: DialogueTree = tree;
      draft.dialogue = t;
      const nodeIds = t.nodes.map((n) => n.id);
      p.appendChild(this.selectBox('Start node', nodeIds.map((n) => ({ v: n, l: n })), t.start, (v) => { t.start = v; }));
      t.nodes.forEach((n, ni) => {
        const card = document.createElement('div'); card.style.cssText = 'border:1px solid var(--ax-line);border-radius:var(--ax-radius);padding:6px;margin:6px 0;background:color-mix(in srgb, var(--ax-bg2) 50%, transparent)';
        const head = document.createElement('div'); head.style.cssText = 'display:flex;gap:4px;align-items:center';
        const idf = this.field('Node id', n.id, (v) => { const old = n.id; n.id = v.replace(/[^a-zA-Z0-9_]/g, '').slice(0, 32) || old; for (const m of t.nodes) for (const c of m.choices) if (c.next === old) c.next = n.id; if (t.start === old) t.start = n.id; }); idf.style.flex = '1';
        head.append(idf, this.btn('✕ node', () => { t.nodes.splice(ni, 1); this.renderPanel(); }, true));
        card.appendChild(head);
        card.appendChild(this.area('NPC says', n.text, (v) => { n.text = v; }, 2));
        card.appendChild(this.field('On show (actions, ; separated)', (n.actions ?? []).join('; '), (v) => { n.actions = v.split(';').map((x) => x.trim()).filter(Boolean); }, 'mem quest=1; state busy; signal gate:open; say Hi; emote wave; goto 10 20'));
        const ch = document.createElement('div'); ch.style.cssText = 'margin-left:8px';
        n.choices.forEach((c, ci) => {
          const crow = document.createElement('div'); crow.style.cssText = 'border-left:2px solid var(--ax-line2);padding-left:6px;margin:4px 0';
          const l1 = document.createElement('div'); l1.style.cssText = 'display:flex;gap:4px';
          const tf = this.field('Player says', c.text, (v) => { c.text = v; }); tf.style.flex = '1';
          const nf = this.selectBox('→ then', [{ v: '', l: '(end)' }, ...nodeIds.map((x) => ({ v: x, l: x }))], c.next, (v) => { c.next = v; }); nf.style.width = '110px';
          l1.append(tf, nf, this.btn('✕', () => { n.choices.splice(ci, 1); this.renderPanel(); }, true));
          crow.appendChild(l1);
          const l2 = document.createElement('div'); l2.style.cssText = 'display:flex;gap:4px';
          const rq = this.field('Only if memory (!key negates)', c.require ?? '', (v) => { c.require = v.trim() || undefined; }, 'helped'); rq.style.flex = '1';
          const ac = this.field('Actions', (c.actions ?? []).join('; '), (v) => { c.actions = v.split(';').map((x) => x.trim()).filter(Boolean); }, 'mem helped=1; signal quest:done'); ac.style.flex = '1';
          l2.append(rq, ac);
          crow.appendChild(l2);
          ch.appendChild(crow);
        });
        ch.appendChild(this.btn('+ choice', () => { n.choices.push({ text: 'Okay.', next: '' }); this.renderPanel(); }));
        card.appendChild(ch);
        p.appendChild(card);
      });
      const tr = document.createElement('div');
      tr.append(this.btn('+ node', () => { let i = t.nodes.length + 1; while (nodeIds.includes(`node${i}`)) i++; t.nodes.push({ id: `node${i}`, text: '', choices: [] }); this.renderPanel(); }),
        this.btn('Remove tree', () => { delete draft.dialogue; this.renderPanel(); }, true));
      p.appendChild(tr);
    }
    // ── Schedule ──
    p.appendChild(this.h('DAY SCHEDULE (no code)'));
    const sched: ScheduleEntry[] = parseSchedule(draft.schedule);
    draft.schedule = sched;
    if (sched.length === 0) p.appendChild(this.note('Rows apply when the world clock reaches their hour: walk somewhere, go home, wander, change the state label, follow a path, say a line.'));
    sched.forEach((e, i) => {
      const r = document.createElement('div'); r.style.cssText = 'display:flex;gap:4px;align-items:flex-end;margin:2px 0';
      const hf = this.selectBox('Hour', Array.from({ length: 24 }, (_, h) => ({ v: String(h), l: `${String(h).padStart(2, '0')}:00` })), String(e.hour), (v) => { e.hour = Number(v); }); hf.style.width = '70px';
      const af = this.selectBox('Action', ['goto', 'home', 'wander', 'state', 'path', 'say'].map((a) => ({ v: a, l: a })), e.action, (v) => { e.action = v as ScheduleEntry['action']; this.renderPanel(); }); af.style.width = '84px';
      r.append(hf, af);
      if (e.action === 'goto') { const xf = this.field('X', String(e.x ?? 0), (v) => { e.x = Number(v) || 0; }); const zf = this.field('Z', String(e.z ?? 0), (v) => { e.z = Number(v) || 0; }); xf.style.width = '60px'; zf.style.width = '60px'; r.append(xf, zf); }
      if (e.action === 'wander') { const xf = this.field('Radius', String(e.x ?? 6), (v) => { e.x = Number(v) || 6; }); xf.style.width = '60px'; r.append(xf); }
      if (e.action === 'state' || e.action === 'path' || e.action === 'say') { const vf = this.field(e.action === 'state' ? 'Label' : e.action === 'path' ? 'Path name' : 'Line', e.value ?? '', (v) => { e.value = v; }); vf.style.flex = '1'; r.append(vf); }
      r.appendChild(this.btn('✕', () => { sched.splice(i, 1); this.renderPanel(); }, true));
      p.appendChild(r);
    });
    const sr = document.createElement('div');
    sr.append(this.btn('+ row', () => { sched.push({ hour: 8, action: 'state', value: 'working' }); this.renderPanel(); }), this.btn('Save NPC data', save));
    p.appendChild(sr);
    p.appendChild(this.note('Scripts can also load these at runtime: npc.dialogue(json) / npc.schedule(json). Pathing: every walk routes around parts and solid devices (grid A*); npc.follow_path("name") walks an NPC path device.'));
  }

  // ─── NPC creator panel ───────────────────────────────────────────────────

  private renderNpcPanel(p: HTMLDivElement, conn: NonNullable<typeof atlas.conn>, wid: bigint): void {
    const sel = this.sel?.kind === 'npc' ? this.sel : null;
    const row = sel ? conn.db.npcUnit.npcId.find(BigInt(sel.id)) : null;
    if (!row) {
      p.appendChild(this.h('NEW NPC'));
      p.appendChild(this.field('Name', this.npcDraft.name, (v) => { this.npcDraft.name = v; }, 'e.g. Dockmaster Veil'));
      p.appendChild(this.selectBox('Body', PLAYER_MODELS.map((m) => ({ v: m.key, l: m.label })), this.npcDraft.modelKey, (v) => { this.npcDraft.modelKey = v; }));
      p.appendChild(this.selectBox('Preset (fills name, script and data)', NPC_PRESETS.map((pr) => ({ v: pr.key, l: pr.label })), this.npcDraft.preset, (v) => { const pr = NPC_PRESETS.find((x) => x.key === v); if (pr) { this.npcDraft.preset = v; if (pr.name) this.npcDraft.name = pr.name; this.npcDraft.template = pr.template; } this.renderPanel(); }));
      p.appendChild(this.selectBox('Starter script', NPC_TEMPLATES.map((t) => ({ v: t.key, l: `${t.label} — ${t.hint}` })), this.npcDraft.template, (v) => { this.npcDraft.template = v; }));
      p.appendChild(this.btn('Place NPC (click the ground)', () => this.setPlaceChoice({ kind: 'npc', key: 'npc', scale: 1 })));
      p.appendChild(this.note('A blank-slate character: no versona brain, no society — it knows exactly what its script reads. Scripts run on every visitor\'s client in the Lua sandbox; "the player" is whoever is looking.'));
      const units = [...conn.db.npcUnit.iter()].filter((u) => u.worldId === wid).sort((a, b) => (a.npcId < b.npcId ? -1 : 1));
      p.appendChild(this.h(`NPCS IN THIS WORLD (${units.length}/32)`));
      if (units.length === 0) p.appendChild(this.note('None yet.'));
      for (const u of units) {
        const r = document.createElement('div'); r.style.cssText = 'display:flex;gap:6px;align-items:center;margin:2px 0';
        const nm = document.createElement('span'); nm.textContent = u.name; nm.style.flex = '1'; // user text → textContent
        const st = document.createElement('span'); st.textContent = u.enabled && u.scriptSrc.trim() ? 'scripted' : 'no script'; st.style.color = u.enabled && u.scriptSrc.trim() ? 'var(--ax-good)' : 'var(--ax-muted)';
        r.append(nm, st, this.btn('Select', () => { const m = this.npcs.unitMesh(u.npcId.toString()); if (m) this.select({ kind: 'npc', id: u.npcId.toString(), mesh: m }); }));
        p.appendChild(r);
      }
      return;
    }
    const id = row.npcId.toString();
    p.appendChild(this.h(`NPC #${id}`));
    let name = row.name, modelKey = row.modelKey, configJson = row.configJson;
    p.appendChild(this.field('Name', name, (v) => { name = v; }));
    p.appendChild(this.selectBox('Body', PLAYER_MODELS.map((m) => ({ v: m.key, l: m.label })), modelKey, (v) => { modelKey = v; }));
    p.appendChild(this.area('Config JSON (npc.config(key))', configJson, (v) => { configJson = v; }, 2, '{ "greeting": "Hi", "radius": 8 }', true));
    const applyRow = document.createElement('div');
    applyRow.append(
      this.btn('Apply name/body/config', () => void conn.reducers.editNpc({ npcId: row.npcId, name: name.trim() || row.name, modelKey, configJson: configJson.trim() || '{}', posX: row.posX, posY: row.posY, posZ: row.posZ, yaw: row.yaw, scale: row.scale })
        .then(() => { this.flash('NPC updated'); setTimeout(() => this.renderPanel(), 250); }).catch((e: Error) => this.flash(e.message))),
      this.btn('Move (2)', () => this.setTool('move')), this.btn('Rotate (3)', () => this.setTool('rotate')), this.btn('Delete', () => void this.deleteSelected(), true),
    );
    p.appendChild(applyRow);
    p.appendChild(this.h(`SCRIPT · ${row.enabled ? 'running' : 'disabled'}`));
    const src = this.npcScriptDraft.get(id) ?? row.scriptSrc;
    const ta = document.createElement('textarea');
    ta.spellcheck = false;
    ta.style.cssText = 'width:100%;height:260px;background:var(--ax-bg2);color:var(--ax-text);border:1px solid var(--ax-line);border-radius:var(--ax-radius);font:12px/1.45 var(--ax-mono);padding:6px;resize:vertical;box-sizing:border-box';
    ta.value = src;
    ta.oninput = () => this.npcScriptDraft.set(id, ta.value);
    ta.onkeydown = (e) => { e.stopPropagation(); if (e.key === 'Tab') { e.preventDefault(); const s0 = ta.selectionStart; ta.setRangeText('  ', s0, ta.selectionEnd, 'end'); this.npcScriptDraft.set(id, ta.value); } };
    p.appendChild(ta);
    const tplRow = document.createElement('div'); tplRow.style.cssText = 'display:flex;gap:6px;align-items:center;margin:4px 0';
    const tpl = document.createElement('select'); tpl.style.cssText = AtlasEditModeModule.INPUT_CSS + ';flex:1';
    for (const t of NPC_TEMPLATES) { const o = document.createElement('option'); o.value = t.key; o.textContent = `${t.label} — ${t.hint}`; tpl.appendChild(o); }
    tplRow.append(tpl, this.btn('Load template', () => { ta.value = npcTemplate(tpl.value); this.npcScriptDraft.set(id, ta.value); }));
    p.appendChild(tplRow);
    const save = (enabled: boolean): void => {
      void conn.reducers.setNpcScript({ npcId: row.npcId, source: ta.value, enabled })
        .then(() => { this.npcScriptDraft.delete(id); this.flash(enabled ? 'Script saved — running for everyone in this world' : 'Script saved (disabled)'); setTimeout(() => this.renderPanel(), 250); })
        .catch((e: Error) => this.flash(`Save refused: ${e.message}`));
    };
    const row2 = document.createElement('div');
    row2.append(this.btn('Save & run', () => save(true)), this.btn(row.enabled ? 'Save & disable' : 'Save disabled', () => save(false)),
      this.btn('Open in Lua IDE (I)', () => bus.emit('atlas:ide-open', { kind: 'npc', npcId: id, name: row.name, source: this.npcScriptDraft.get(id) })), // an unsaved panel draft carries over
      this.btn(this.showApiRef ? 'Hide API reference' : 'API reference', () => { this.showApiRef = !this.showApiRef; this.renderPanel(); }));
    p.appendChild(row2);
    p.appendChild(this.note('Return a table of hooks (on_init, on_tick, on_player_enter/leave, on_interact, on_talk_start/end, on_say, on_topic, on_choice, on_arrive, on_signal, on_versona_reply, on_hour). Errors show in the Lua IDE console (I); 3 strikes disable the script locally.'));
    this.renderNpcDataEditors(p, conn, row);
    if (this.showApiRef) {
      const filter = document.createElement('input'); filter.placeholder = 'filter the npc.* reference…'; filter.style.cssText = AtlasEditModeModule.INPUT_CSS + ';margin:4px 0';
      const list = document.createElement('div'); list.style.cssText = 'max-height:300px;overflow:auto;font:11px/1.4 var(--ax-font)';
      const entries = apiFor('worldscript').filter((e) => e.name.startsWith('npc.') || /^on_(player_enter|player_leave|talk_start|talk_end|say|topic|choice|arrive|signal|versona_reply|hour|interact|init|tick)$/.test(e.name));
      const render = (): void => {
        list.innerHTML = '';
        const q = filter.value.toLowerCase();
        for (const e of entries) {
          if (q && !e.name.includes(q) && !e.doc.toLowerCase().includes(q)) continue;
          const d = document.createElement('div'); d.style.cssText = 'margin:3px 0;padding:3px 4px;border-left:2px solid var(--ax-line)';
          const sig = document.createElement('div'); sig.textContent = e.sig; sig.style.cssText = 'color:var(--ax-hi);font:600 11px var(--ax-mono);cursor:pointer';
          sig.title = 'Click to insert';
          sig.onclick = () => { const ins = e.insert ?? e.name + '()'; const s0 = ta.selectionStart; ta.setRangeText(ins.replace('${}', ''), s0, ta.selectionEnd, 'end'); this.npcScriptDraft.set(id, ta.value); ta.focus(); };
          const doc = document.createElement('div'); doc.textContent = e.doc; doc.style.color = 'var(--ax-soft)';
          d.append(sig, doc); list.appendChild(d);
        }
      };
      filter.oninput = render; render();
      p.append(filter, list);
    }
  }

  // ─── Versona placement panel ─────────────────────────────────────────────

  private versonaForm(p: HTMLDivElement, d: VersonaDraft, commons: boolean): void {
    p.appendChild(this.selectBox('Archetype (body + idle behaviour)', VERSONA_ARCHETYPES.map((a) => ({ v: a, l: a })), d.archetype, (v) => { d.archetype = v; }));
    if (commons) {
      p.appendChild(this.note('On the Commons a placed versona IS a settler: it joins the nearest settlement (within 400 m) and carries the full Commons context — job, economy, memories, war, the lore — exactly like its neighbours. Nothing to author here.'));
      return;
    }
    p.appendChild(this.field('Name', d.name, (v) => { d.name = v; }, 'e.g. Archivist Senna'));
    p.appendChild(this.area('Characteristics — who they are, how they speak', d.persona, (v) => { d.persona = v; }, 3, 'Dry, precise, kind underneath. Former ship navigator. Never raises her voice.'));
    p.appendChild(this.area('Context — what they know about THIS world', d.context, (v) => { d.context = v; }, 4, 'This is the Glasswater research outpost. The reactor is down since the storm. The lift to the observatory needs a keycard from the foreman.'));
    p.appendChild(this.field('Greeting (blank = composed)', d.greeting, (v) => { d.greeting = v; }, 'Back again? The lift is still broken.'));
    p.appendChild(this.area('Topics — one per line: Label :: reply', d.topicsText, (v) => { d.topicsText = v; }, 4, 'The reactor :: Down since the storm. The foreman keeps saying tomorrow.\nThe observatory :: You need the foreman\'s keycard for the lift.'));
    p.appendChild(this.h('DETERMINISM'));
    p.appendChild(this.check('Free text may reach the LLM', d.llm, (v) => { d.llm = v; }, 'Off = topics only; anything else gets the deflect line'));
    p.appendChild(this.check('Strict — answer ONLY from the context/topics', d.strict, (v) => { d.strict = v; }, 'On = the LLM may not invent; unknown questions get the deflect line'));
    p.appendChild(this.field('Deflect line', d.deflect, (v) => { d.deflect = v; }));
    p.appendChild(this.slider('Temperature', 0, 1, 0.05, d.temperature, (v) => { d.temperature = v; }));
    p.appendChild(this.slider('Max words', 10, 120, 5, d.maxWords, (v) => { d.maxWords = v; }));
    p.appendChild(this.slider('Wander (m)', 0, 60, 1, d.wanderM, (v) => { d.wanderM = v; }));
    p.appendChild(this.note('0 wander = stands where placed. Topics answer instantly and identically for everyone; the LLM lane is only for free text the topics don\'t cover.'));
  }

  private renderVersonaPanel(p: HTMLDivElement, conn: NonNullable<typeof atlas.conn>, wid: bigint): void {
    const commons = this.isCommons();
    if (!this.caps.admin) { p.appendChild(this.note('Placing versonas needs the owner/admin capability in this world (build grants place NPCs, not versonas).')); return; }
    const sel = this.sel?.kind === 'versona' ? this.sel : null;
    const prof = sel ? conn.db.versonaProfile.versonaId.find(BigInt(sel.id)) : null;
    if (prof) {
      p.appendChild(this.h(`VERSONA #${sel!.id}${prof.custom ? ' · authored' : ' · Commons settler'}`));
      const d: VersonaDraft = {
        name: prof.name, archetype: prof.archetype, persona: prof.persona, context: prof.context, greeting: prof.greeting, deflect: prof.deflect,
        topicsText: topicsJsonToText(prof.topicsJson), llm: prof.llm, strict: prof.strict, temperature: prof.temperature, maxWords: prof.maxWords, wanderM: prof.wanderM,
      };
      this.versonaForm(p, d, !prof.custom);
      const row = document.createElement('div');
      row.append(
        this.btn('Save profile', () => void conn.reducers.setVersonaProfile({
          versonaId: prof.versonaId, name: d.name.trim(), archetype: d.archetype, persona: d.persona, context: d.context, greeting: d.greeting, deflect: d.deflect,
          topicsJson: topicsTextToJson(d.topicsText), llm: d.llm, strict: d.strict, temperature: d.temperature, maxWords: d.maxWords, wanderM: d.wanderM,
        }).then(() => this.flash('Versona profile saved')).catch((e: Error) => this.flash(`Save refused: ${e.message}`))),
        this.btn('Move (2)', () => this.setTool('move')), this.btn('Delete', () => void this.deleteSelected(), true),
      );
      p.appendChild(row);
      return;
    }
    p.appendChild(this.h(commons ? 'PLACE A SETTLER' : 'NEW VERSONA'));
    this.versonaForm(p, this.versonaDraft, commons);
    p.appendChild(this.btn('Place versona (click the ground)', () => this.setPlaceChoice({ kind: 'versona', key: 'versona', scale: 1 })));
    const placed = [...conn.db.versonaProfile.iter()].filter((r) => r.worldId === wid).sort((a, b) => (a.versonaId < b.versonaId ? -1 : 1));
    p.appendChild(this.h(`PLACED VERSONAS (${placed.length}/24)`));
    if (placed.length === 0) p.appendChild(this.note('None yet. Placed versonas show as violet markers while you edit — click one to select it.'));
    for (const r of placed) {
      const line = document.createElement('div'); line.style.cssText = 'display:flex;gap:6px;align-items:center;margin:2px 0';
      const nm = document.createElement('span'); nm.textContent = r.name || `${r.archetype} #${r.versonaId}`; nm.style.flex = '1'; // user text
      line.append(nm, this.btn('Select', () => { const m = this.npcs.markerFor(r.versonaId.toString()); if (m) this.select({ kind: 'versona', id: r.versonaId.toString(), mesh: m }); else this.flash('Marker not in view yet — look toward it'); }));
      p.appendChild(line);
    }
  }

  dispose(): void {
    this.exit();
    if (this.gizmoHelper && this.scene) this.scene.remove(this.gizmoHelper);
    this.gizmo?.dispose();
    if (this.brushRing && this.scene) { this.scene.remove(this.brushRing); this.brushRing.geometry.dispose(); }
  }
}
